export type Language = "zh" | "en";

type TranslationMap = Record<string, { zh: string; en: string }>;

// ─── Navbar ───
const navbar: TranslationMap = {
  "nav.llms": { zh: "Science LLMs", en: "Science LLMs" },
  "nav.llms.desc": { zh: "科学大语言模型", en: "Scientific Large Language Models" },
  "nav.models": { zh: "Compute Models", en: "Compute Models" },
  "nav.models.desc": { zh: "细分领域科研小模型", en: "Domain-Specific Research Models" },
  "nav.data": { zh: "Data Hub", en: "Data Hub" },
  "nav.data.desc": { zh: "专有科学数据库", en: "Proprietary Scientific Databases" },
  "nav.scinexus": { zh: "SciNexus", en: "SciNexus" },
  "nav.scinexus.desc": { zh: "AI 科研工作台", en: "AI Research Workbench" },
  "nav.docs": { zh: "Docs", en: "Docs" },
  "nav.docs.desc": { zh: "开发者文档", en: "Developer Documentation" },
  "nav.console": { zh: "控制台", en: "Console" },
  "nav.getApiKey": { zh: "获取 API Key", en: "Get API Key" },
  "nav.login": { zh: "登录", en: "Sign In" },
  "nav.uptime": { zh: "99.9% 可用性", en: "99.9% Uptime" },
  "nav.logout": { zh: "登出", en: "Logout" },
  "nav.switchToLight": { zh: "切换到清新风", en: "Switch to Light" },
  "nav.switchToDark": { zh: "切换到科技风", en: "Switch to Dark" },
};

// ─── Footer ───
const footer: TranslationMap = {
  "footer.brand": { zh: "ScienceToken 是晶泰科技即将推出的 AI for Science 开放平台，将科学计算能力封装为可调用的 Token 服务。", en: "ScienceToken is an upcoming AI for Science platform by XtalPi, encapsulating scientific computing capabilities into callable Token services." },
  "footer.platform": { zh: "平台", en: "Platform" },
  "footer.developers": { zh: "开发者", en: "Developers" },
  "footer.company": { zh: "公司", en: "Company" },
  "footer.legal": { zh: "法律", en: "Legal" },
  "footer.documentation": { zh: "文档", en: "Documentation" },
  "footer.apiReference": { zh: "API 参考", en: "API Reference" },
  "footer.quickstart": { zh: "快速开始", en: "Quickstart" },
  "footer.aboutXtalPi": { zh: "关于晶泰科技", en: "About XtalPi" },
  "footer.blog": { zh: "博客", en: "Blog" },
  "footer.careers": { zh: "招聘", en: "Careers" },
  "footer.contact": { zh: "联系我们", en: "Contact" },
  "footer.privacy": { zh: "隐私政策", en: "Privacy Policy" },
  "footer.terms": { zh: "服务条款", en: "Terms of Service" },
  "footer.dataSecurity": { zh: "数据安全", en: "Data Security" },
  "footer.copyright": { zh: "© 2026 ScienceToken. 保留所有权利。", en: "© 2026 ScienceToken. All rights reserved." },
  "footer.allSystems": { zh: "所有系统运行正常", en: "All systems operational" },
  "footer.comingSoon": { zh: "即将上线", en: "Coming soon" },
  "footer.comingSoonDesc": { zh: "页面正在建设中。", en: "page is under construction." },
};

// ─── Home Page ───
const home: TranslationMap = {
  "home.badge": { zh: "现已支持 XtalPi Science Flagship LLM", en: "Now supporting XtalPi Science Flagship LLM" },
  "home.headline.1": { zh: "The Unified API", en: "The Unified API" },
  "home.headline.2": { zh: "AI in Science", en: "AI in Science" },
  "home.subtitle": {
    zh: "ScienceToken -- 将晶泰科技的AI for Science的核心能力封装为可调用的Token，驱动下一代科学发现。让每个人都能用上顶级科学 AI。",
    en: "ScienceToken -- Encapsulating XtalPi's core AI for Science capabilities into callable Tokens, powering the next generation of scientific discovery. Making top-tier scientific AI accessible to everyone.",
  },
  "home.getApiKey": { zh: "立刻体验", en: "Try Now" },
  "home.exploreModels": { zh: "探索 Science LLMs", en: "Explore Science LLMs" },
  "home.stats.llms": { zh: "科学大模型", en: "Science LLMs" },
  "home.stats.context": { zh: "最大上下文", en: "Max Context" },
  "home.stats.uptime": { zh: "服务可用性", en: "Uptime" },
  "home.stats.latency": { zh: "平均延迟", en: "Avg Latency" },
  "home.stats.formats": { zh: "科学格式支持", en: "Science Formats" },
  "home.stats.sdk": { zh: "SDK 兼容", en: "SDK Compatible" },
  "home.features.title": { zh: "融合科学计算能力的大语言模型", en: "LLMs with Integrated Scientific Computing" },
  "home.features.subtitle": { zh: "集成蛋白质结构预测、分子对接、ADMET 预测和科学数据库能力的科学大语言模型，一个 API 即可调用。", en: "Scientific LLMs integrating protein structure prediction, molecular docking, ADMET prediction, and scientific database capabilities. One API call." },
  "home.features.flagship.desc": {
    zh: "晶泰科技自研旗舰科学大语言模型，集成蛋白质结构预测、分子性质分析、逆合成规划等科学计算能力，原生理解 SMILES、FASTA 等科学格式，128K 超长上下文。",
    en: "XtalPi's proprietary flagship scientific LLM with integrated protein structure prediction, molecular property analysis, retrosynthesis planning capabilities. Natively understands SMILES, FASTA formats. 128K context window.",
  },
  "home.features.standard.desc": {
    zh: "晶泰科技标准版科学大语言模型，在旗舰模型基础上优化推理成本，适用于大批量分子性质预测和科学问答，性价比极高。",
    en: "XtalPi's standard scientific LLM, optimized for inference cost based on the flagship model. Ideal for large-batch molecular property prediction and scientific QA with exceptional cost-effectiveness.",
  },
  "home.learnMore": { zh: "了解更多", en: "Learn more" },
  "home.code.badge": { zh: "Science, Tokenized", en: "Science, Tokenized" },
  "home.code.title": { zh: "一切科学能力，皆为", en: "All Science Capabilities as" },
  "home.code.desc": {
    zh: "蛋白质结构预测、分子对接、ADMET 预测、科学数据库检索——我们将这些科学计算能力全部融合到大语言模型中，封装为标准化的 Tokenized 服务。一个 API Key，统一调用，按 Token 精确计量。",
    en: "Protein structure prediction, molecular docking, ADMET prediction, scientific database retrieval — we've integrated all these scientific computing capabilities into LLMs, encapsulated as standardized Tokenized services. One API key, unified access, precise token-based metering.",
  },
  "home.code.feature1": { zh: "兼容 Anthropic SDK，零迁移成本", en: "Anthropic SDK compatible, zero migration cost" },
  "home.code.feature2": { zh: "支持流式响应 (Streaming)", en: "Streaming responses supported" },
  "home.code.feature3": { zh: "Python, Node.js, cURL 多语言支持", en: "Python, Node.js, cURL multi-language support" },
  "home.code.feature4": { zh: "原生理解 SMILES、FASTA 科学格式", en: "Natively understands SMILES, FASTA scientific formats" },
  "home.code.quickstart": { zh: "查看快速入门指南", en: "View Quickstart Guide" },
  "home.scinexus.badge": { zh: "无需编程", en: "No Coding Required" },
  "home.scinexus.title": { zh: "认识 SciNexus — 您的科研助手 Agent", en: "Meet SciNexus — Your Research Assistant Agent" },
  "home.scinexus.desc": {
    zh: "用自然语言描述您的科学目标。SciNexus Agent 将编排 Protenix、DiffDock、XADMET、IOv4 等模型和数据库，交付端到端结果及 3D 可视化。",
    en: "Describe your scientific goal in plain language. SciNexus Agent will orchestrate Protenix, DiffDock, XADMET, IOv4 and other models and databases to deliver end-to-end results with 3D visualization.",
  },
  "home.scinexus.tpl1": { zh: "靶点到先导化合物筛选", en: "Target-to-Lead Screening" },
  "home.scinexus.tpl2": { zh: "蛋白质序列设计与验证", en: "Protein Sequence Design & Validation" },
  "home.scinexus.tpl3": { zh: "材料性质预测与筛选", en: "Material Property Prediction & Screening" },
  "home.scinexus.tpl4": { zh: "文献驱动的假设生成", en: "Literature-Driven Hypothesis Generation" },
  "home.scinexus.launch": { zh: "启动 SciNexus", en: "Launch SciNexus" },
  "home.pricing.badge": { zh: "统一定价", en: "Unified Pricing" },
  "home.pricing.title": { zh: "一种货币覆盖所有科学计算", en: "One Currency for All Scientific Computing" },
  "home.pricing.desc": {
    zh: "ScienceToken (ST) 是统一的货币，将 GPU 算力时长、LLM Token 和数据库查询整合到一套透明的计费系统中。",
    en: "ScienceToken (ST) is the unified currency that bridges GPU compute time, LLM tokens, and database queries into a single, transparent billing system.",
  },
  "home.pricing.viewBilling": { zh: "查看计费详情", en: "View Billing Details" },
  "home.pricing.perTokens": { zh: "每 1M tokens", en: "per 1M tokens" },
  "home.pricing.perTask": { zh: "每任务", en: "per task" },
  "home.pricing.per100q": { zh: "每 100 次查询", en: "per 100 queries" },
  "home.pricing.perWorkflow": { zh: "每工作流", en: "per workflow" },
  "home.pricing.aggregated": { zh: "按量聚合", en: "Aggregated" },
  "home.partners": { zh: "受领先科研机构和生物技术公司信赖", en: "Trusted by leading research institutions and biotech companies" },
  "home.cta.title": { zh: "立即开始构建", en: "Start Building Today" },
  "home.cta.desc": {
    zh: "60 秒内获取你的 API Key。无需信用卡即可开始。免费额度包含 10,000 ScienceTokens。",
    en: "Get your API key in 60 seconds. No credit card required to start. Free tier includes 10,000 ScienceTokens.",
  },
  "home.cta.free": { zh: "立刻加入", en: "Join Now" },
  "home.cta.docs": { zh: "阅读文档", en: "Read the Docs" },
};

// ─── Science LLMs Page ───
const llms: TranslationMap = {
  "llms.hero.desc": {
    zh: "XtalPi Science Flagship 和 Standard 是您直接调用的模型——原生理解 SMILES、FASTA、InChI 等科学格式。它们背后有 200+ 自研专有算法作为计算基座。",
    en: "XtalPi Science Flagship and Standard are the models you call directly — natively understanding SMILES, FASTA, InChI and more. Behind them, 200+ proprietary algorithms form the computational foundation.",
  },
  "llms.search": { zh: "搜索模型...", en: "Search models..." },
  "llms.noResults": { zh: "没有匹配的模型。", en: "No models match your search." },
  "llms.comparison": { zh: "模型对比", en: "Model Comparison" },
  "llms.bestFor": { zh: "最适用于", en: "Best For" },
};

// ─── Compute Models Page ───
const models: TranslationMap = {
  "models.hero.desc": {
    zh: "细分领域科研小模型：蛋白质结构预测、分子对接、材料性质预测、物理模拟。按 GPU 算力和数据规模精确计费，统一 API 调用。",
    en: "Domain-specific research models for protein structure prediction, molecular docking, material property prediction, and physics simulation. Precise billing based on GPU compute and data scale, unified API access.",
  },
  "models.search": { zh: "搜索模型、任务...", en: "Search models, tasks..." },
  "models.available": { zh: "个模型可用", en: "models available" },
  "models.pricing.title": { zh: "计算模型定价逻辑", en: "Compute Model Pricing Logic" },
  "models.pricing.desc": {
    zh: "计算模型按 GPU 算力时长 × 输入数据规模 计费，与 LLM 的 Token 计费逻辑不同。每个模型的基础费率已标注在卡片中，实际费用 = 基础费率 × 序列长度/分子数量系数。",
    en: "Compute models are billed by GPU compute time × input data scale, which differs from LLM token-based pricing. Each model's base rate is shown on the card. Actual cost = base rate × sequence length/molecule count coefficient.",
  },
  "models.pricing.protein": { zh: "蛋白质结构", en: "Protein Structure" },
  "models.pricing.docking": { zh: "分子对接", en: "Molecular Docking" },
  "models.pricing.property": { zh: "性质预测", en: "Property Prediction" },
  "models.pricing.pde": { zh: "PDE 模拟", en: "PDE Simulation" },
};

// ─── Data Hub Page ───
const data: TranslationMap = {
  "data.hero.desc": {
    zh: "晶泰科技专有科学数据库，覆盖有机化学反应、材料科学、小分子生物活性和 ADMET 数据。标准化 RESTful API，学术与商业授权可选。",
    en: "XtalPi's proprietary scientific databases covering organic chemical reactions, materials science, small molecule bioactivity, and ADMET data. Standardized RESTful API with academic and commercial licensing options.",
  },
  "data.search": { zh: "搜索数据库...", en: "Search databases..." },
  "data.apiTitle": { zh: "统一数据访问 API", en: "Unified Data Access API" },
  "data.apiDesc": {
    zh: "所有数据库通过统一的 RESTful API 访问，使用相同的认证机制和返回格式，轻松集成到现有的数据分析流程中。",
    en: "All databases are accessed through a unified RESTful API with the same authentication mechanism and response format, easily integrating into existing data analysis workflows.",
  },
  "data.apiFeat1": { zh: "支持精确查询、相似性搜索和批量导出", en: "Supports exact query, similarity search, and bulk export" },
  "data.apiFeat2": { zh: "返回标准化 JSON 格式，含来源文献引用", en: "Returns standardized JSON format with source literature citations" },
  "data.apiFeat3": { zh: "支持流式订阅获取最新数据更新", en: "Supports streaming subscriptions for latest data updates" },
  "data.apiFeat4": { zh: "与 SciNexus Agent 深度集成", en: "Deep integration with SciNexus Agent" },
};

// ─── SciNexus Page ───
const scinexus: TranslationMap = {
  "scinexus.badge": { zh: "科研助手 Agent", en: "Research Assistant Agent" },
  "scinexus.headline.1": { zh: "科学研究，", en: "Science Research," },
  "scinexus.headline.2": { zh: "AI 自动化", en: "Automated by AI" },
  "scinexus.desc": {
    zh: "用自然语言描述您的科研目标，SciNexus Agent 将自动编排 Protenix、XtalFold2、DiffDock、XADMET、IOv4 等模型和专有数据库，完成端到端的计算工作流，并生成可交互的 3D 可视化报告。",
    en: "Describe your research goal in natural language. SciNexus Agent will automatically orchestrate Protenix, XtalFold2, DiffDock, XADMET, IOv4 and other models and proprietary databases to complete end-to-end computational workflows and generate interactive 3D visualization reports.",
  },
  "scinexus.feat1": { zh: "无需编程基础，自然语言驱动", en: "No coding required, natural language driven" },
  "scinexus.feat2": { zh: "自动选择最优模型组合", en: "Automatically selects optimal model combinations" },
  "scinexus.feat3": { zh: "内置 3D 分子可视化", en: "Built-in 3D molecular visualization" },
  "scinexus.feat4": { zh: "一键导出 PDF 研究报告", en: "One-click PDF research report export" },
  "scinexus.feat5": { zh: "完整的实验日志和可复现性", en: "Complete experiment logs and reproducibility" },
  "scinexus.launch": { zh: "启动 SciNexus", en: "Launch SciNexus" },
  "scinexus.viewDocs": { zh: "查看文档", en: "View Docs" },
  "scinexus.chatInput": { zh: "描述您的科研任务...", en: "Describe your scientific task..." },
  "scinexus.templates.title": { zh: "内置工作流模板", en: "Built-in Workflow Templates" },
  "scinexus.templates.subtitle": { zh: "从预设模板快速开始，或用自然语言自定义工作流", en: "Start quickly from preset templates, or customize workflows with natural language" },
  "scinexus.howTitle": { zh: "SciNexus 工作原理", en: "How SciNexus Works" },
  "scinexus.step1.title": { zh: "描述目标", en: "Describe Goal" },
  "scinexus.step1.desc": { zh: "用中文或英文描述您的科研问题，SciNexus 理解科学语境。", en: "Describe your research question in any language. SciNexus understands scientific context." },
  "scinexus.step2.title": { zh: "自动规划", en: "Auto Planning" },
  "scinexus.step2.desc": { zh: "Agent 分解任务，选择最优模型组合，生成执行计划。", en: "The agent breaks down tasks, selects optimal model combinations, and generates an execution plan." },
  "scinexus.step3.title": { zh: "并行执行", en: "Parallel Execution" },
  "scinexus.step3.desc": { zh: "多个计算任务并行执行，实时显示进度和中间结果。", en: "Multiple compute tasks execute in parallel with real-time progress and intermediate results." },
  "scinexus.step4.title": { zh: "可视化报告", en: "Visual Report" },
  "scinexus.step4.desc": { zh: "生成 3D 结构可视化和结构化研究报告，支持 PDF 导出。", en: "Generates 3D structure visualization and structured research reports with PDF export." },
  "scinexus.cta.title": { zh: "准备好加速您的研究了吗？", en: "Ready to Accelerate Your Research?" },
  "scinexus.cta.desc": { zh: "SciNexus 已集成在您的 ScienceToken 控制台中，无需额外配置。", en: "SciNexus is integrated in your ScienceToken console. No additional setup required." },
  "scinexus.cta.open": { zh: "打开 SciNexus Agent", en: "Open SciNexus Agent" },
};

// ─── NotFound Page ───
const notFound: TranslationMap = {
  "notFound.title": { zh: "页面未找到", en: "Page Not Found" },
  "notFound.desc": {
    zh: "您访问的页面不存在或已被移动。请尝试返回首页或浏览我们的平台。",
    en: "The page you're looking for doesn't exist or has been moved. Try navigating back to the homepage or exploring our platform.",
  },
  "notFound.back": { zh: "返回首页", en: "Back to Home" },
  "notFound.explore": { zh: "探索模型", en: "Explore Models" },
};

// ─── Console Layout ───
const console_layout: TranslationMap = {
  "console.overview": { zh: "概览", en: "OVERVIEW" },
  "console.developer": { zh: "科学模型库", en: "SCIENCE MODELS" },
  "console.sciModelLib": { zh: "科学模型库", en: "SCIENCE MODELS" },
  "console.workspace": { zh: "工作空间", en: "WORKSPACE" },
  "console.account": { zh: "账户", en: "ACCOUNT" },
  "console.dashboard": { zh: "仪表盘", en: "Dashboard" },
  "console.apiKeys": { zh: "API 密钥", en: "API Keys" },
  "console.sciLLMs": { zh: "通用 Science LLMs", en: "General Science LLMs" },
  "console.computeModels": { zh: "Compute Models", en: "Compute Models" },
  "console.dataHub": { zh: "Data Hub", en: "Data Hub" },
  "console.scinexusAgent": { zh: "SciNexus Agent", en: "SciNexus Agent" },
  "console.experimentLogs": { zh: "调用日志", en: "Call Logs" },
  "console.billing": { zh: "计费", en: "Billing" },
  "console.team": { zh: "团队", en: "Team" },
  "console.settings": { zh: "设置", en: "Settings" },
  "console.tokenBalance": { zh: "Token 余额", en: "Token Balance" },
  "console.backToSite": { zh: "← 返回网站", en: "← Back to Site" },
  "console.admin": { zh: "管理员", en: "ADMIN" },
  "console.adminTeams": { zh: "团队管理", en: "Team Management" },
  "console.adminUpstreamModels": { zh: "Upstream 模型", en: "Upstream Models" },
  "console.adminScienceLLMs": { zh: "ScienceLLM 产品", en: "ScienceLLM Products" },
  "console.adminProviders": { zh: "供应商", en: "Providers" },
  "console.adminInsights": { zh: "洞察", en: "Insights" },
  "console.adminMargin": { zh: "毛利预估", en: "Margin" },
  "console.adminUsage": { zh: "全部调用日志", en: "All Call Logs" },
  "console.adminUsageAttempts": { zh: "选址尝试日志", en: "Usage Attempts" },
  "console.adminBills": { zh: "账单管理", en: "Bill Management" },
  "console.adminWalletTransactions": { zh: "充值和消费记录", en: "Recharge & Usage Records" },
  "console.adminAuditLogs": { zh: "审计日志", en: "Audit Logs" },
  "console.adminEmailConfig": { zh: "邮件配置", en: "Email Config" },
  "console.adminEmailStatus": { zh: "邮件发送记录", en: "Email Sending Records" },
  "console.adminGroup.monitoring": { zh: "调用监控", en: "Call Monitoring" },
  "console.adminGroup.finance": { zh: "财务管理", en: "Finance" },
  "console.adminGroup.analytics": { zh: "经营分析", en: "Business Analytics" },
  "console.adminGroup.models": { zh: "模型与供应商管理", en: "Models & Providers" },
  "console.adminGroup.system": { zh: "系统设置与审计", en: "System & Audit" },
  "console.adminDashboard": { zh: "管理员仪表盘", en: "Admin Dashboard" },
  "console.workspace.enterAdmin": { zh: "进入平台管理后台", en: "Enter Admin Console" },
  "console.workspace.backToConsole": { zh: "返回业务控制台", en: "Back to Business Console" },
  "console.teamGroup": { zh: "团队管理", en: "TEAM" },
  "console.teamDashboard": { zh: "团队仪表盘", en: "Team Dashboard" },
  "console.teamManage": { zh: "团队成员管理", en: "Member Management" },
  "console.teamBills": { zh: "团队账单记录", en: "Team Bills" },
  "console.teamUsage": { zh: "团队调用日志", en: "Team Call Logs" },
  "console.teamWalletTransactions": { zh: "充值和消费记录", en: "Recharge & Usage Records" },
};

// ─── Admin Console ───
const admin: TranslationMap = {
  "admin.badge": { zh: "系统管理员", en: "System Admin" },
  "admin.teams.title": { zh: "团队管理", en: "Team Management" },
  "admin.teams.subtitle": { zh: "管理所有团队、添加团队管理员、调整钱包与配额", en: "Manage teams, assign team admins, adjust wallets and quotas" },
  "admin.teams.newTeam": { zh: "新建团队", en: "New Team" },
  "admin.teams.empty": { zh: "尚未创建任何团队", en: "No teams yet" },
  "admin.teams.searchPlaceholder": { zh: "搜索团队名…", en: "Search teams…" },
  "admin.teams.colName": { zh: "团队", en: "Team" },
  "admin.teams.colOwner": { zh: "所有者", en: "Owner" },
  "admin.teams.colWallet": { zh: "钱包余额", en: "Wallet" },
  "admin.teams.colQuota": { zh: "配额", en: "Quota" },
  "admin.teams.colProviders": { zh: "供应商 (已配/总数)", en: "Providers (set/total)" },
  "admin.teams.colMembers": { zh: "成员数 / 上限", en: "Members / Limit" },
  "admin.teams.colCreated": { zh: "创建时间", en: "Created" },
  "admin.teams.addAdmin": { zh: "添加管理员", en: "Add Admin" },
  "admin.teams.recharge": { zh: "充值", en: "Recharge" },
  "admin.teams.updateQuota": { zh: "更新配额", en: "Update Quota" },
  "admin.teams.viewMembers": { zh: "查看成员", en: "View Members" },
  "admin.teams.modalNewTitle": { zh: "新建团队", en: "Create Team" },
  "admin.teams.modalAddAdminTitle": { zh: "添加团队管理员", en: "Add Team Admin" },
  "admin.teams.modalAddAdminHint": { zh: "通过邮箱将现有用户提升为该团队的管理员，用户将被移动到此团队", en: "Promote an existing user (by email) to team_admin of this team. The user will be moved into this team." },
  "admin.teams.modalRechargeTitle": { zh: "调整 ScienceToken 余额", en: "Adjust ScienceToken Balance" },
  "admin.teams.opType": { zh: "操作类型", en: "Operation" },
  "admin.teams.opTypeRecharge": { zh: "充值", en: "Recharge" },
  "admin.teams.opTypeDeduct": { zh: "扣款", en: "Deduct" },
  "admin.teams.rechargedToast": { zh: "充值成功", en: "Recharge successful" },
  "admin.teams.deductedToast": { zh: "扣款成功", en: "Deduction successful" },
  "admin.teams.deductHint": { zh: "扣款会减少团队余额，允许扣至负数。", en: "Deduction reduces the team balance and is allowed to go negative." },
  "admin.teams.modalQuotaTitle": { zh: "更新配额", en: "Update Quota" },
  "admin.teams.modalMembersTitle": { zh: "团队成员", en: "Team Members" },
  "admin.teams.name": { zh: "团队名称", en: "Team Name" },
  "admin.teams.email": { zh: "用户邮箱", en: "User Email" },
  "admin.teams.amount": { zh: "充值金额 (USD)", en: "Amount (USD)" },
  "admin.teams.note": { zh: "备注 (可选)", en: "Note (optional)" },
  "admin.teams.tokenLimit": { zh: "TPM 限额", en: "TPM Limit" },
  "admin.teams.requestLimit": { zh: "RPM 限额", en: "RPM Limit" },
  "admin.teams.memberLimit": { zh: "人员上限", en: "Member Limit" },
  "admin.teams.unlimited": { zh: "(空 = 无限)", en: "(empty = unlimited)" },
  "admin.teams.noOwner": { zh: "(无)", en: "(none)" },
  "admin.teams.noMembers": { zh: "暂无活跃成员", en: "No active members" },
  "admin.teams.creditLimit": { zh: "信用额度", en: "Credit Limit" },
  "admin.teams.debt": { zh: "欠款", en: "Debt" },
  "admin.teams.setCreditLimit": { zh: "设置信用额度", en: "Set Credit Limit" },
  "admin.teams.creditLimitHint": { zh: "允许团队余额透支到的最大额度，0 表示不允许透支。", en: "Maximum amount the team balance may go negative by. 0 means no overdraft allowed." },
  "admin.teams.creditLimitUpdatedToast": { zh: "信用额度已更新", en: "Credit limit updated" },
  "admin.teams.bills": { zh: "账单", en: "Bills" },
  "admin.teams.modalBillingConfigTitle": { zh: "计费配置", en: "Billing Config" },
  "admin.teams.billingConfigHint": { zh: "折扣立即生效于后续计费请求；出账周期变更从下一出账周期起生效。", en: "The discount applies immediately to subsequent billed requests; a billing cycle change takes effect starting the next cycle." },
  "admin.teams.generateBill": { zh: "手动出账", en: "Generate Bill" },
  "admin.teams.modalGenerateBillTitle": { zh: "手动出账", en: "Generate Bill" },
  "admin.teams.periodStartLabel": { zh: "账期起始日期", en: "Period Start Date" },
  "admin.teams.periodEndLabel": { zh: "账期结束", en: "Period end" },
  "admin.teams.generateBillNotDueHint": { zh: "当前账期尚未结束，暂无可出账的账期", en: "The current period has not ended yet — nothing to bill." },
  "admin.teams.generateBillSuccessToast": { zh: "账单已生成", en: "Bill generated" },
  "admin.teams.generateBillNoneToast": { zh: "无透支，未出账或账单已存在", en: "No overdraft to bill, or the bill already exists" },
  "admin.teams.createdUser": { zh: "用户", en: "User" },
  "admin.teams.createdEmail": { zh: "邮箱", en: "Email" },
  "admin.teams.createdPassword": { zh: "密码", en: "Password" },
  "admin.teams.copyPassword": { zh: "复制密码", en: "Copy password" },
  "admin.teams.addAdminSuccessToast": { zh: "{name} 已提升为团队管理员", en: "{name} promoted to team_admin" },
  "admin.teams.addAdminFailed": { zh: "添加管理员失败", en: "Add admin failed" },
  "admin.teams.addAdminEmailHint": { zh: "若该邮箱未注册，将自动创建用户并生成初始密码。", en: "If unregistered, a new user will be created with a generated password." },
  "admin.teams.errUserInOtherTeam": {
    zh: "该用户已属于其他团队，暂不支持直接转移到本团队。",
    en: "This user already belongs to another team; cross-team transfer is not supported.",
  },
  "admin.teams.errUserNotActive": {
    zh: "该用户未处于激活状态，无法设为团队管理员。",
    en: "This user is not active and cannot be made a team admin.",
  },
  "admin.teams.errSysadminCannotBeTeamAdmin": {
    zh: "系统管理员不能被设为团队管理员。",
    en: "System admins cannot be assigned as team admins.",
  },

  // Bill management (standalone admin page)
  "admin.bills.title": { zh: "账单管理", en: "Bill Management" },
  "admin.bills.subtitle": { zh: "查看各团队的出账记录，标记结清或作废，导出明细", en: "Review issued bills across teams, mark them paid or void, and export details" },
  "admin.bills.allTeams": { zh: "全部团队", en: "All Teams" },
  "admin.bills.clearTeamFilter": { zh: "清除团队筛选", en: "Clear team filter" },
  "admin.bills.allStatus": { zh: "全部状态", en: "All" },
  "admin.bills.statusUnpaid": { zh: "未结清", en: "Unpaid" },
  "admin.bills.statusPaid": { zh: "已结清", en: "Paid" },
  "admin.bills.statusVoid": { zh: "已作废", en: "Void" },
  "admin.bills.records": { zh: "条账单", en: "bills" },
  "admin.bills.colTeam": { zh: "团队", en: "Team" },
  "admin.bills.colPeriod": { zh: "账期", en: "Period" },
  "admin.bills.colTotal": { zh: "总消费", en: "Total Consumption" },
  "admin.bills.colAmountDue": { zh: "应付额", en: "Amount Due" },
  "admin.bills.colStatus": { zh: "状态", en: "Status" },
  "admin.bills.colIssuedAt": { zh: "出账时间", en: "Issued At" },
  "admin.bills.colActions": { zh: "操作", en: "Actions" },
  "admin.bills.colModel": { zh: "模型 (science_llm_key)", en: "Model (science_llm_key)" },
  "admin.bills.colCalls": { zh: "调用次数", en: "Calls" },
  "admin.bills.colListAmount": { zh: "原价金额", en: "List Amount" },
  "admin.bills.colItemAmount": { zh: "折后金额", en: "Amount (After Discount)" },
  "admin.bills.empty": { zh: "暂无账单", en: "No bills found" },
  "admin.bills.noItems": { zh: "无分项数据", en: "No line items" },
  "admin.bills.lineItems": { zh: "按模型明细", en: "Line Items" },
  "admin.bills.summaryTotalList": { zh: "原价总额", en: "List Amount (Total)" },
  "admin.bills.summaryDiscount": { zh: "商务折扣减免", en: "Discount Reduction" },
  "admin.bills.summaryVoucherOffset": { zh: "代金券抵扣", en: "Voucher Offset" },
  "admin.bills.summaryCashOffset": { zh: "现金抵扣", en: "Cash Offset" },
  "admin.bills.summarySubtotalCredit": {
    zh: "本期信用消费",
    en: "Credit Consumed (This Period)",
  },
  "admin.bills.summaryAmountDue": { zh: "应付金额", en: "Amount Due" },
  "admin.bills.summaryNoteBasis": {
    zh: "口径说明：代金券抵扣 / 现金抵扣 / 本期信用消费 按每请求实际扣费拆分（优先级 代金券 → 现金余额 → 信用额度），三段精确相加 = 原价总额 − 商务折扣减免，故上方为本期「消费」口径。应付金额另按账户「净欠款增量」计算（账期末余额相对历史未付账单的增量），会计入账期内的充值、还款与历史结转，因此与「本期信用消费」口径不同、金额不一定相等——例如账期内充值现金会先抵消已发生的信用欠款，使应付低于本期信用消费。",
    en: "Basis: “Voucher offset / Cash offset / Credit consumed” are split by each request's actual funding (priority: voucher → cash balance → credit limit); the three segments sum exactly to the list amount minus the discount reduction — i.e. the block above is the period's *consumption* view. Amount Due is computed separately as the net increase in outstanding balance (end-of-period balance relative to prior unpaid bills); it accounts for recharges, payments and carried-over balance within the period, so it uses a different basis from “Credit consumed” and need not be equal — e.g. a cash recharge during the period first offsets already-incurred credit debt, making Amount Due lower than Credit consumed.",
  },
  "admin.bills.summaryNoteDiscount": {
    zh: "折扣说明：账单只展示折扣减免金额（= 原价 − 折后，精确，不受账期内折扣调整影响）；账单不显示折扣率。当前生效折扣见团队钱包/概览页，逐请求折扣见用量日志。",
    en: "Discount note: the bill only shows the discount reduction amount (= list price − discounted price, exact, unaffected by mid-period discount changes); the discount rate itself is not shown. See the team wallet/overview page for the currently effective discount, and the usage log for the per-request discount.",
  },
  "admin.bills.markPaid": { zh: "标记已结清", en: "Mark Paid" },
  "admin.bills.void": { zh: "作废", en: "Void" },
  "admin.bills.export": { zh: "导出", en: "Export" },
  "admin.bills.note": { zh: "备注", en: "Note" },
  "admin.bills.paidToast": { zh: "账单已标记为已结清", en: "Bill marked as paid" },
  "admin.bills.voidedToast": { zh: "账单已作废", en: "Bill voided" },
  "admin.bills.regenerate": { zh: "重新出具", en: "Regenerate" },
  "admin.bills.regeneratedToast": { zh: "账单已重新出具", en: "Bill regenerated" },
  "admin.bills.regenerateConfirmTitle": { zh: "确认重新出具", en: "Confirm Regenerate" },
  "admin.bills.regenerateConfirmBody": { zh: "确认重新出具该账期的账单？将按该账期重新计算并覆盖原作废账单；若与其它账单账期重叠将被拒绝。", en: "Regenerate this period's bill? It will be recomputed and overwrite the voided bill; it is rejected if the period overlaps another bill." },
  "admin.bills.payConfirmTitle": { zh: "确认标记已结清", en: "Confirm Mark Paid" },
  "admin.bills.payConfirmBody": { zh: "确认将该账单标记为已结清？此操作不可撤销。", en: "Mark this bill as paid? This cannot be undone." },
  "admin.bills.voidConfirmTitle": { zh: "确认作废账单", en: "Confirm Void" },
  "admin.bills.voidConfirmBody": { zh: "确认作废该账单？作废后不可恢复。", en: "Void this bill? This cannot be undone." },
  "admin.bills.preview": { zh: "预览账单", en: "Preview Bill" },
  "admin.bills.previewTitle": { zh: "预览账单", en: "Preview Bill" },
  "admin.bills.previewNotice": {
    zh: "预览 · 未出账，不计入账单簿",
    en: "Preview only — not issued, not recorded in the bill ledger",
  },
  "admin.bills.previewSelectTeam": { zh: "请选择团队", en: "Select a team" },
  "admin.bills.previewStart": { zh: "起始日期（含）", en: "Start Date (inclusive)" },
  "admin.bills.previewEnd": { zh: "结束日期（含）", en: "End Date (inclusive)" },
  "admin.bills.previewSubmit": { zh: "生成预览", en: "Generate Preview" },
  "admin.bills.previewRangeError": {
    zh: "起始日期不能晚于结束日期",
    en: "Start date cannot be after end date",
  },
  "admin.bills.previewRequiredError": { zh: "请填写团队与起止日期", en: "Team and date range are required" },

  // ─── Wallet transactions (充值记录), shared column/type labels for both
  // the admin (cross-team) and team (own-team) pages ───
  "wallet.txn.colTime": { zh: "时间", en: "Time" },
  "wallet.txn.colTeam": { zh: "团队", en: "Team" },
  "wallet.txn.colType": { zh: "类型", en: "Type" },
  "wallet.txn.colAmount": { zh: "金额", en: "Amount" },
  "wallet.txn.colDimension": { zh: "扣费维度", en: "Funding Source" },
  "wallet.txn.colBalanceAfter": { zh: "余额（后）", en: "Balance After" },
  "wallet.txn.colOperator": { zh: "操作人", en: "Operator" },
  "wallet.txn.colNote": { zh: "备注", en: "Note" },
  "wallet.txn.allTypes": { zh: "全部类型", en: "All Types" },
  "wallet.txn.typeRecharge": { zh: "现金充值", en: "Cash Recharge" },
  "wallet.txn.typeAdjust": { zh: "现金调整", en: "Cash Adjust" },
  "wallet.txn.typePayment": { zh: "账单扣款", en: "Bill Payment" },
  "wallet.txn.typeVoucherRecharge": { zh: "代金券充值", en: "Voucher Recharge" },
  "wallet.txn.typeVoucherAdjust": { zh: "代金券调整", en: "Voucher Adjust" },
  "wallet.txn.typeSettle": { zh: "调用结算", en: "Usage Settlement" },
  "wallet.txn.typeArchiveWriteoff": { zh: "归档核销", en: "Archive Write-off" },
  "wallet.txn.empty": { zh: "暂无充值和消费记录", en: "No transactions found" },
  "wallet.txn.resetFilters": { zh: "重置筛选", en: "Reset Filters" },

  "admin.walletTxn.title": { zh: "充值和消费记录", en: "Recharge & Usage Records" },
  "admin.walletTxn.subtitle": { zh: "查看各团队的现金与代金券充值、扣款、结算流水", en: "Review cash and voucher recharges, deductions, and settlement entries across teams" },
  "admin.walletTxn.allTeams": { zh: "全部团队", en: "All Teams" },

  "team.walletTxn.title": { zh: "充值和消费记录", en: "Recharge & Usage Records" },
  "team.walletTxn.subtitle": { zh: "本团队的现金与代金券充值、扣款、结算流水", en: "Cash and voucher recharges, deductions, and settlement entries for your team" },

  "admin.upstream.title": { zh: "Upstream 模型", en: "Upstream Models" },
  "admin.upstream.subtitle": { zh: "管理真实的上游 LLM 端点 — base_url、API key、协议、规格和价格", en: "Manage real upstream LLM endpoints — base_url, API key, protocols, capacity, and pricing" },
  "admin.upstream.new": { zh: "新建 Upstream", en: "New Upstream" },
  "admin.upstream.empty": { zh: "尚未配置任何 Upstream 模型", en: "No upstream models configured yet" },
  "admin.upstream.edit": { zh: "编辑", en: "Edit" },
  "admin.upstream.deactivate": { zh: "下线", en: "Deactivate" },
  "admin.upstream.test": { zh: "测试", en: "Test" },
  "admin.upstream.testRunning": { zh: "测试中…", en: "Testing…" },
  "admin.upstream.testTooltip": { zh: "对每个声明的协议发送一次最小请求，验证 base_url + api_key + upstream_model 是否可用", en: "Send a minimal request to each declared protocol — verifies base_url, api_key, and upstream_model are working" },
  "admin.upstream.confirmDeactivate": { zh: "确定要下线该 Upstream 吗？引用它的 ScienceLLM 路由将失败。", en: "Deactivate this upstream? ScienceLLMs routing to it will start failing." },
  "admin.upstream.pricingSection": { zh: "价格 (成本 + 售卖价)", en: "Pricing (Cost + Sale Rate)" },
  "admin.upstream.modalCreateTitle": { zh: "新建 Upstream 模型", en: "Create Upstream Model" },
  "admin.upstream.modalEditTitle": { zh: "编辑 Upstream 模型", en: "Edit Upstream Model" },
  "admin.upstream.fieldName": { zh: "显示名称", en: "Display Name" },
  "admin.upstream.fieldKey": { zh: "model_key (内部标识，创建后不可改)", en: "model_key (internal id, locked after create)" },
  "admin.upstream.fieldDesc": { zh: "描述", en: "Description" },
  "admin.upstream.fieldProvider": { zh: "Provider (anthropic / openai)", en: "Provider (anthropic / openai)" },
  "admin.upstream.fieldBaseURL": { zh: "base_url (上游 API 根)", en: "base_url (upstream API root)" },
  "admin.upstream.fieldAPIKey": { zh: "API Key", en: "API Key" },
  "admin.upstream.apiKeyEditHint": { zh: "留空则保留当前已加密的 Key", en: "Leave blank to keep the currently stored key" },
  "admin.upstream.revealApiKey": { zh: "查看 API Key", en: "Reveal API Key" },
  "admin.upstream.hideApiKey": { zh: "隐藏 API Key", en: "Hide API Key" },
  "admin.upstream.apiKeyMissing": { zh: "未配置 API Key", en: "No API Key configured" },
  "admin.upstream.fieldUpstreamModel": { zh: "upstream_model (向上游发送的 model 字段)", en: "upstream_model (the model field sent upstream)" },
  "admin.upstream.fieldProtocols": { zh: "协议 (首项 = 原生协议)", en: "Protocols (first = native)" },
  "admin.upstream.protocolsHint": { zh: "选中的第一项为原生协议；其他协议由桥接层翻译。", en: "First selected protocol is native; others are translated by the bridge layer." },
  "admin.upstream.fieldCtx": { zh: "Context Window (tokens)", en: "Context Window (tokens)" },
  "admin.upstream.fieldTpm": { zh: "TPM 限额 (空 = 不限, 0 = 硬封禁)", en: "TPM Limit (empty = unlimited, 0 = hard block)" },
  "admin.upstream.fieldRpm": { zh: "RPM 限额 (空 = 不限, 0 = 硬封禁)", en: "RPM Limit (empty = unlimited, 0 = hard block)" },
  "admin.upstream.costGroup": { zh: "上游成本 (USD/Ktok)", en: "Upstream Cost (USD/Ktok)" },
  "admin.upstream.saleGroup": { zh: "ScienceToken 售卖价 (USD/Ktok)", en: "ScienceToken Sale Rate (USD/Ktok)" },
  "admin.upstream.priceInput": { zh: "输入", en: "Input" },
  "admin.upstream.priceOutput": { zh: "输出", en: "Output" },
  "admin.upstream.priceCacheWrite": { zh: "缓存写入", en: "Cache Write" },
  "admin.upstream.priceCacheRead": { zh: "缓存读取", en: "Cache Read" },
  "admin.common.cancel": { zh: "取消", en: "Cancel" },
  "admin.common.create": { zh: "创建", en: "Create" },
  "admin.common.update": { zh: "更新", en: "Update" },
  "admin.common.save": { zh: "保存", en: "Save" },
  "admin.common.required": { zh: "必填", en: "Required" },

  // Admin team detail page (/console/admin/teams/:tid)
  "admin.teamDetail.back": { zh: "返回团队列表", en: "Back to Teams" },
  "admin.teamDetail.notFound": { zh: "团队不存在", en: "Team not found" },
  "admin.teamDetail.loadFailed": { zh: "加载团队失败", en: "Failed to load team" },
  "admin.teamDetail.basicInfo": { zh: "基本信息", en: "Basic Info" },
  "admin.teamDetail.name": { zh: "名称", en: "Name" },
  "admin.teamDetail.teamId": { zh: "团队 ID", en: "Team ID" },
  "admin.teamDetail.status": { zh: "状态", en: "Status" },
  "admin.teamDetail.archived": { zh: "已归档", en: "Archived" },
  "admin.teamDetail.walletBalance": { zh: "钱包余额", en: "Wallet Balance" },
  "admin.teamDetail.createdAt": { zh: "创建时间", en: "Created At" },
  "admin.teamDetail.rateLimits": { zh: "模型限速", en: "Rate Limits" },
  "admin.teamDetail.rateLimitsHint": { zh: "按模型配置 RPM/TPM 上限", en: "Per-model RPM/TPM limits" },
  "admin.teamDetail.configure": { zh: "配置限速", en: "Configure" },
  "admin.teamDetail.walletOps": { zh: "钱包操作", en: "Wallet" },
  "admin.teamDetail.rechargeDeduct": { zh: "充值 / 扣减", en: "Recharge / Deduct" },
  "admin.teamDetail.voucher": { zh: "发放代金券", en: "Grant Voucher" },
  "admin.teamDetail.bills": { zh: "账单", en: "Bills" },
  "admin.teamDetail.generateBill": { zh: "生成账单", en: "Generate Bill" },
  "admin.teamDetail.viewBills": { zh: "查看账单列表", en: "View Bills" },
  "admin.teamDetail.members": { zh: "成员", en: "Members" },
  "admin.teamDetail.addAdmin": { zh: "添加管理员", en: "Add Admin" },
  "admin.teamDetail.manageMembers": { zh: "管理成员", en: "Manage Members" },
  "admin.teamDetail.quota": { zh: "配额 · 成员上限", en: "Quota · Member Limit" },
  "admin.teamDetail.quotaPlaceholder": { zh: "留空=不限", en: "Blank = unlimited" },
  "admin.teamDetail.quotaInvalid": { zh: "成员上限须为正整数", en: "Member limit must be a positive integer" },
  "admin.teamDetail.quotaBelowMembers": { zh: "成员上限不能小于当前成员数（{count}）", en: "Member limit cannot be below the current member count ({count})" },
  "admin.teamDetail.quotaUpdated": { zh: "配额已更新", en: "Quota updated" },
  "admin.teamDetail.creditLimit": { zh: "信用额度 (USD)", en: "Credit Limit (USD)" },
  "admin.teamDetail.creditInvalid": { zh: "信用额度须为非负数", en: "Credit limit must be non-negative" },
  "admin.teamDetail.creditUpdated": { zh: "信用额度已更新", en: "Credit limit updated" },
  "admin.teamDetail.billingConfig": { zh: "计费配置", en: "Billing Config" },
  "admin.teamDetail.billingLoadFailed": { zh: "加载计费配置失败", en: "Failed to load billing config" },
  "admin.teamDetail.discountRate": { zh: "折扣率 (0-1)", en: "Discount Rate (0-1)" },
  "admin.teamDetail.discountRequired": { zh: "折扣率不能为空", en: "Discount rate is required" },
  "admin.teamDetail.discountInvalid": { zh: "折扣率须为 0-1 之间的小数", en: "Discount rate must be a decimal in (0, 1]" },
  "admin.teamDetail.cycle": { zh: "计费周期", en: "Billing Cycle" },
  "admin.teamDetail.cycleMonthly": { zh: "按月（每月 1 号 ~ 次月 1 号 / 自然月）", en: "Monthly (1st → 1st)" },
  "admin.teamDetail.cycleWeekly": { zh: "按周（周一 ~ 周日）", en: "Weekly (Mon–Sun)" },
  "admin.teamDetail.cycleHintMonthly": { zh: "出账区间：每月 1 号 ~ 次月 1 号（自然月）", en: "Billing period: 1st → 1st each month (natural month)" },
  "admin.teamDetail.cycleHintWeekly": { zh: "出账区间：周一 ~ 周日", en: "Billing period: Mon–Sun" },
  "admin.teamDetail.billingUpdated": { zh: "计费配置已更新", en: "Billing config updated" },
  "admin.teamDetail.allowlist": { zh: "供应商白名单", en: "Provider Allowlist" },
  "admin.teamDetail.noProviders": { zh: "暂无可用供应商", en: "No providers available" },
  "admin.teamDetail.providersLoadFailed": { zh: "加载供应商失败", en: "Failed to load providers" },
  "admin.teamDetail.allowlistEmptyWarn": { zh: "清空后该团队将看不到任何模型", en: "With none selected, this team sees no models" },
  "admin.teamDetail.allowlistUpdated": { zh: "供应商白名单已更新", en: "Provider allowlist updated" },
  "admin.teams.showArchived": { zh: "显示已归档", en: "Show archived" },
  "admin.teams.archived": { zh: "已归档", en: "Archived" },

  // ScienceLLM (product-level model) admin
  "admin.sciLLM.title": { zh: "ScienceLLM 产品", en: "ScienceLLM Products" },
  "admin.sciLLM.subtitle": { zh: "对外可见的产品级模型 — 按权重路由到一个或多个上游模型 (允许跨 provider，但协议必须一致)", en: "User-facing product models — weighted routing to one or more upstream models (cross-provider allowed, protocol must match)" },
  "admin.sciLLM.new": { zh: "新建 ScienceLLM", en: "New ScienceLLM" },
  "admin.sciLLM.empty": { zh: "尚未创建任何 ScienceLLM 产品", en: "No ScienceLLM products yet" },
  "admin.sciLLM.editLimits": { zh: "编辑限额", en: "Edit Limits" },
  "admin.sciLLM.editRouting": { zh: "配置路由", en: "Configure Routing" },
  "admin.sciLLM.deactivate": { zh: "下线", en: "Deactivate" },
  "admin.sciLLM.confirmDeactivate": { zh: "确定要下线该 ScienceLLM 吗？用户将无法再使用其 model_key 调用。", en: "Deactivate this ScienceLLM? Users will no longer be able to call its model_key." },
  "admin.sciLLM.deactivateTitle": { zh: "下线 ScienceLLM", en: "Deactivate ScienceLLM" },
  "admin.sciLLM.reactivate": { zh: "重新上线", en: "Reactivate" },
  "admin.sciLLM.confirmReactivate": { zh: "确定要重新上线该 ScienceLLM 吗？用户将可以通过其 model_key 再次调用。", en: "Reactivate this ScienceLLM? Users will be able to call its model_key again." },
  "admin.sciLLM.reactivateTitle": { zh: "重新上线 ScienceLLM", en: "Reactivate ScienceLLM" },
  "admin.sciLLM.reactivateSuccess": { zh: "ScienceLLM 已重新上线", en: "ScienceLLM reactivated" },
  "admin.sciLLM.reactivateFailed": { zh: "重新上线失败", en: "Failed to reactivate" },
  "admin.sciLLM.deactivateSuccess": { zh: "ScienceLLM 已下线", en: "ScienceLLM deactivated" },
  "admin.sciLLM.deactivateFailed": { zh: "下线失败", en: "Failed to deactivate" },
  "admin.sciLLM.routingSection": { zh: "上游路由", en: "Upstream Routing" },
  "admin.sciLLM.routingEmpty": { zh: "未配置 — 调用将失败", en: "Not configured — calls will fail" },
  "admin.sciLLM.modalCreateTitle": { zh: "新建 ScienceLLM 产品", en: "Create ScienceLLM Product" },
  "admin.sciLLM.modalEditTitle": { zh: "编辑 ScienceLLM 限额", en: "Edit ScienceLLM Limits" },
  "admin.sciLLM.modalRoutingTitle": { zh: "配置上游路由", en: "Configure Upstream Routing" },
  "admin.sciLLM.routingHint": { zh: "勾选要纳入路由的上游模型，并为每个设置权重 (按权重随机选)。允许混合不同 provider，但协议必须与 ScienceLLM 一致 — 协议不匹配的候选已禁用，运行时无桥接翻译。", en: "Pick upstream models to include in routing and set each weight (weighted-random selection). Mixing providers is allowed, but the protocol must match the ScienceLLM — mismatched candidates are disabled, no cross-protocol bridging at runtime." },
  "admin.sciLLM.fieldName": { zh: "显示名称", en: "Display Name" },
  "admin.sciLLM.fieldKey": { zh: "model_key (用户填入的标识)", en: "model_key (what users send)" },
  "admin.sciLLM.fieldDesc": { zh: "描述", en: "Description" },
  "admin.sciLLM.fieldCtx": { zh: "Context Window (tokens)", en: "Context Window (tokens)" },
  "admin.sciLLM.fieldTpm": { zh: "TPM 限额 (空 = 不限, 0 = 硬封禁)", en: "TPM Limit (empty = unlimited, 0 = hard block)" },
  "admin.sciLLM.fieldRpm": { zh: "RPM 限额 (空 = 不限, 0 = 硬封禁)", en: "RPM Limit (empty = unlimited, 0 = hard block)" },
  "admin.sciLLM.colInclude": { zh: "启用", en: "Include" },
  "admin.sciLLM.colModelKey": { zh: "上游 model_key", en: "Upstream model_key" },
  "admin.sciLLM.colProvider": { zh: "Provider", en: "Provider" },
  "admin.sciLLM.colProtocols": { zh: "协议", en: "Protocols" },
  "admin.sciLLM.colWeight": { zh: "权重", en: "Weight" },
  "admin.sciLLM.noMatchingModels": { zh: "暂无活跃的上游模型 — 请先在「Upstream 模型」页面创建", en: "No active upstream models — create them on the Upstream Models page first" },
  "admin.providerModels.rpmLimit": { zh: "RPM 限额", en: "RPM Limit" },
  "admin.providerModels.tpmLimit": { zh: "TPM 限额", en: "TPM Limit" },
  "admin.providerModels.unlimited": { zh: "无限制", en: "Unlimited" },
  "admin.providerModels.quotaHelp": { zh: "仅作展示使用的运行指标限额；不强制拦截请求", en: "Display-only operational limit; not enforced" },
  "admin.providerModels.capabilities": { zh: "能力", en: "Capabilities" },
  "admin.providerModels.capabilitiesHelp": { zh: "模型开放哪些代理端点；至少选择一项", en: "Which proxy endpoints this model serves; at least one required" },
  "admin.providerModels.capabilitiesRequired": { zh: "至少选择一项能力", en: "Select at least one capability" },
  "admin.providerModels.capabilitiesAnthropicHint": { zh: "anthropic 协议仅支持 message", en: "anthropic protocol only supports message" },
  "admin.providerModels.imageProbeCostWarning": { zh: "图片能力探测会真实调用 Images API（约 $0.01/次）", en: "Image capability probing makes a real Images API call (~$0.01 per run)" },
  "admin.common.delete": { zh: "彻底删除", en: "Delete" },
  "admin.provider.confirmDelete": { zh: "确定要彻底删除该 Provider 吗？此操作不可恢复，且仅限非活跃 Provider。", en: "Permanently delete this Provider? This action cannot be undone and only applies to inactive providers." },
  "admin.provider.deleteTitle": { zh: "删除 Provider", en: "Delete Provider" },
  "admin.provider.deleteSuccess": { zh: "Provider 已删除", en: "Provider deleted" },
  "admin.provider.deleteFailed": { zh: "删除失败", en: "Failed to delete provider" },
  "admin.provider.deactivate": { zh: "下线", en: "Deactivate" },
  "admin.provider.confirmDeactivate": { zh: "确定要下线该 Provider 吗？其下的 Model 将开始失败。", en: "Deactivate this Provider? Models under it will fail." },
  "admin.provider.deactivateTitle": { zh: "下线 Provider", en: "Deactivate Provider" },
  "admin.provider.deactivateSuccess": { zh: "Provider 已下线", en: "Provider deactivated" },
  "admin.provider.deactivateFailed": { zh: "下线失败", en: "Failed to deactivate" },
  "admin.providerModel.confirmDelete": { zh: "确定要彻底删除该 Provider Model 吗？此操作不可恢复，且仅限非活跃 Model。", en: "Permanently delete this Provider Model? This action cannot be undone and only applies to inactive models." },
  "admin.providerModel.deleteTitle": { zh: "删除 Provider Model", en: "Delete Provider Model" },
  "admin.providerModel.deleteSuccess": { zh: "Model 已删除", en: "Model deleted" },
  "admin.providerModel.deleteFailed": { zh: "删除失败", en: "Failed to delete model" },
  "admin.providerModel.deactivateSuccess": { zh: "Model 已下线", en: "Model deactivated" },
  "admin.providerModel.deactivateFailed": { zh: "下线失败", en: "Failed to deactivate" },
  "admin.providerModel.deactivate": { zh: "下线", en: "Deactivate" },
  "admin.providerModel.confirmDeactivate": { zh: "确定要下线该 Provider Model 吗？调用它的 ScienceLLM 路由将开始失败。", en: "Deactivate this Provider Model? ScienceLLMs routing to it will start failing." },
  "admin.providerModel.deactivateTitle": { zh: "下线 Provider Model", en: "Deactivate Provider Model" },
  "admin.providerModel.reactivate": { zh: "重新上线", en: "Reactivate" },
  "admin.providerModel.confirmReactivate": { zh: "确定要重新上线该 Provider Model 吗？", en: "Reactivate this Provider Model?" },
  "admin.providerModel.reactivateTitle": { zh: "重新上线 Provider Model", en: "Reactivate Provider Model" },
  "admin.providerModel.reactivateSuccess": { zh: "Model 已重新上线", en: "Model reactivated" },
  "admin.providerModel.reactivateFailed": { zh: "重新上线失败", en: "Failed to reactivate" },
  "admin.providerModel.test": { zh: "测试", en: "Test" },
  "admin.providerModel.testRunning": { zh: "测试中…", en: "Testing…" },
  "admin.providerModel.testSuccess": { zh: "模型测试成功", en: "Model test passed" },
  "admin.providerModel.testFailed": { zh: "模型测试失败", en: "Model test failed" },
  "admin.providerModel.testTooltip": { zh: "测试模型可用性", en: "Test model availability" },
  "admin.providerModel.testCancel": { zh: "点击取消测试", en: "Click to cancel test" },
  "admin.sciLLM.confirmDelete": { zh: "确定要彻底删除该 ScienceLLM 吗？此操作不可恢复，且仅限非活跃 ScienceLLM。", en: "Permanently delete this ScienceLLM? This action cannot be undone and only applies to inactive ScienceLLMs." },
  "admin.sciLLM.deleteTitle": { zh: "删除 ScienceLLM", en: "Delete ScienceLLM" },
  "admin.sciLLM.deleteSuccess": { zh: "ScienceLLM 已删除", en: "ScienceLLM deleted" },
  "admin.sciLLM.deleteFailed": { zh: "删除失败", en: "Failed to delete ScienceLLM" },
  "admin.sciLLM.delete": { zh: "彻底删除", en: "Delete" },

  "admin.auditLogs.title": { zh: "管理员审计日志", en: "Admin Audit Logs" },
  "admin.auditLogs.subtitle": { zh: "查看所有管理员操作的审计记录（只读）", en: "View a read-only audit trail of all admin actions" },
  "admin.auditLogs.colTime": { zh: "时间", en: "Time" },
  "admin.auditLogs.colActor": { zh: "操作者", en: "Actor" },
  "admin.auditLogs.colAction": { zh: "操作", en: "Action" },
  "admin.auditLogs.colTarget": { zh: "目标", en: "Target" },
  "admin.auditLogs.colOutcome": { zh: "结果", en: "Outcome" },
  "admin.auditLogs.colClientIP": { zh: "客户端 IP", en: "Client IP" },
  "admin.auditLogs.colRequestID": { zh: "请求 ID", en: "Request ID" },
  "admin.auditLogs.filterTargetType": { zh: "目标类型", en: "Target Type" },
  "admin.auditLogs.filterOutcome": { zh: "所有结果", en: "All Outcomes" },
  "admin.auditLogs.filterAction": { zh: "操作 (Action)", en: "Action" },
  "admin.auditLogs.filterActorUserId": { zh: "操作者 User ID", en: "Actor User ID" },
  "admin.auditLogs.filterFrom": { zh: "开始时间", en: "From" },
  "admin.auditLogs.filterTo": { zh: "结束时间", en: "To" },
  "admin.auditLogs.allTargetTypes": { zh: "所有类型", en: "All Types" },
  "admin.auditLogs.outcomeSuccess": { zh: "成功", en: "Success" },
  "admin.auditLogs.outcomeFailure": { zh: "失败", en: "Failure" },
  "admin.auditLogs.empty": { zh: "暂无审计日志", en: "No audit logs found" },
  "admin.auditLogs.sysAdmin": { zh: "系统管理员", en: "sysadmin" },
  "admin.auditLogs.metadata": { zh: "元数据", en: "Metadata" },
  "admin.auditLogs.changes": { zh: "变更", en: "Changes" },
  "admin.auditLogs.colField": { zh: "字段", en: "Field" },
  "admin.auditLogs.colBefore": { zh: "变更前", en: "Before" },
  "admin.auditLogs.colAfter": { zh: "变更后", en: "After" },
  "admin.auditLogs.errorDetail": { zh: "错误详情", en: "Error Detail" },

  // Admin email configuration page (/console/admin/email-config)
  "admin.emailConfig.title": { zh: "邮件配置", en: "Email Configuration" },
  "admin.emailConfig.subtitle": { zh: "为每类事务邮件配置额外的抄送收件人列表与启用开关（全局生效，不区分团队）", en: "Configure extra CC recipient lists and an enabled toggle for each transactional email scenario (global, not per-team)" },
  "admin.emailConfig.scenario.account_activation": { zh: "账号激活", en: "Account Activation" },
  "admin.emailConfig.scenario.member_activation": { zh: "成员账号开通", en: "Member Account Activation" },
  "admin.emailConfig.scenario.password_reset": { zh: "密码重置", en: "Password Reset" },
  "admin.emailConfig.scenario.quota_alert": { zh: "配额告警", en: "Quota Alert" },
  "admin.emailConfig.scenario.low_balance": { zh: "余额不足提醒", en: "Low Balance" },
  "admin.emailConfig.desc.account_activation": { zh: "新建团队管理员账号时发送给该团队管理员", en: "Sent to the team admin when a new team administrator account is created" },
  "admin.emailConfig.desc.member_activation": { zh: "团队管理员邀请新成员并创建账号时发送给该成员", en: "Sent to a member when a team admin invites them and their account is created" },
  "admin.emailConfig.desc.password_reset": { zh: "管理员重置某用户密码时发送给该用户", en: "Sent to a user when an administrator resets their password" },
  "admin.emailConfig.desc.quota_alert": { zh: "用量达到 80% / 90% / 100% 阈值时发送给团队管理员", en: "Sent to team administrators when usage crosses 80% / 90% / 100%" },
  "admin.emailConfig.desc.low_balance": { zh: "预付费团队（无信用额度）余额与代金券合计低于 10 ST 时发送给团队管理员", en: "Sent to team administrators when a prepaid team's cash balance plus vouchers drops below 10 ST" },
  "admin.emailConfig.enabled": { zh: "启用抄送", en: "Enable CC" },
  "admin.emailConfig.enabledHint": { zh: "关闭后该场景邮件仍会发送给主收件人，仅不再抄送下方列表", en: "When off, the email still goes to the primary recipient — it just skips the CC list below" },
  "admin.emailConfig.ccList": { zh: "抄送列表", en: "CC List" },
  "admin.emailConfig.ccPlaceholder": { zh: "邮箱地址", en: "Email address" },
  "admin.emailConfig.addRecipient": { zh: "添加收件人", en: "Add recipient" },
  "admin.emailConfig.empty": { zh: "暂无抄送收件人", en: "No CC recipients" },
  "admin.emailConfig.invalidEmail": { zh: "存在无效的邮箱地址：{email}", en: "Invalid email address: {email}" },
  "admin.emailConfig.saved": { zh: "邮件配置已保存", en: "Email config saved" },
  "admin.emailConfig.loadFailed": { zh: "加载邮件配置失败", en: "Failed to load email config" },
  "admin.emailConfig.saveFailed": { zh: "保存邮件配置失败", en: "Failed to save email config" },

  // Admin email send status page (/console/admin/email-status)
  "admin.emailStatus.title": { zh: "邮件发件状态", en: "Email Send Status" },
  "admin.emailStatus.subtitle": { zh: "查看邮件发件箱与发送状态", en: "View email outbox and delivery status" },
  "admin.emailStatus.allStatuses": { zh: "全部状态", en: "All Statuses" },
  "admin.emailStatus.allScenarios": { zh: "全部场景", en: "All Scenarios" },
  "admin.emailStatus.filterByRecipient": { zh: "收件人邮箱", en: "Recipient email" },
  "admin.emailStatus.recipients": { zh: "收件人", en: "Recipients" },
  "admin.emailStatus.retries": { zh: "重试", en: "Retries" },
  "admin.emailStatus.lastError": { zh: "最后错误", en: "Last Error" },
  "admin.emailStatus.params": { zh: "参数", en: "Params" },
  "admin.emailStatus.nextAttempt": { zh: "下次尝试", en: "Next attempt" },
  "admin.emailStatus.sentAt": { zh: "发送于", en: "Sent at" },
  "admin.emailStatus.deadAt": { zh: "失败于", en: "Dead at" },
  "admin.emailStatus.inFlight": { zh: "处理中", en: "In flight" },
  "admin.emailStatus.scenario": { zh: "场景", en: "Scenario" },
  "admin.emailStatus.status": { zh: "状态", en: "Status" },
  "admin.emailStatus.time": { zh: "时间", en: "Time" },
  "admin.emailStatus.team": { zh: "团队", en: "Team" },

};

// ─── Dashboard ───
const dashboard: TranslationMap = {
  "dash.title": { zh: "仪表盘", en: "Dashboard" },
  "dash.subtitle": { zh: "API 使用情况和活动概览", en: "Overview of your API usage and activity" },
  "dash.weekly": { zh: "本周", en: "This Week" },
  "dash.monthly": { zh: "本月", en: "This Month" },
  "dash.weeklyUsage": { zh: "本周用量", en: "This Week's Usage" },
  "dash.monthlyUsage": { zh: "本月用量", en: "This Month's Usage" },
  "dash.scopeWeek": { zh: "自然周口径", en: "Calendar-week basis" },
  "dash.scopeMonth": { zh: "自然月口径", en: "Calendar-month basis" },
  "dash.quickSelect": { zh: "快速选择", en: "Quick Select" },
  "dash.teamFilterAll": { zh: "全部团队", en: "All Teams" },
  "dash.teamFilterSearch": { zh: "搜索团队…", en: "Search teams…" },
  "dash.assetPrepaid": { zh: "预存余额", en: "Prepaid Balance" },
  "dash.assetVoucher": { zh: "代金券余额", en: "Voucher Balance" },
  "dash.assetCreditAvail": { zh: "可用信用", en: "Available Credit" },
  "dash.assetDebt": { zh: "欠款待还", en: "Outstanding Debt" },
  "dash.assetPayable": { zh: "应付未付", en: "Payable" },
  "dash.assetOverviewTitle": { zh: "团队资产概览", en: "Team Asset Overview" },
  "dash.teamInfoPanel": { zh: "团队信息", en: "Team Info" },
  "dash.walletInfoPanel": { zh: "团队钱包信息", en: "Team Wallet" },
  "dash.walletPanel": { zh: "钱包信息", en: "Wallet" },
  "dash.memberQuota": { zh: "成员配额", en: "Member Quota" },
  "dash.goToMembers": { zh: "前往团队成员管理", en: "Go to member management" },
  "dash.usageLabel": { zh: "已用", en: "Usage" },
  "dash.billingConfigSection": { zh: "团队商务配置", en: "Team Billing Configuration" },
  "dash.assetArrears": { zh: "欠费（待还）", en: "Amount Overdue" },
  "dash.newApiKey": { zh: "新建 API Key", en: "New API Key" },
  "dash.totalTokens": { zh: "总 Token 数", en: "Total Tokens" },
  "dash.apiCalls": { zh: "API 调用", en: "API Calls" },
  "dash.estCost": { zh: "预计费用", en: "Est. Cost" },
  "dash.balance": { zh: "余额", en: "Balance" },
  "dash.usageBreakdown": { zh: "Token 使用量分布", en: "Token Usage Breakdown" },
  "dash.dailyUsage": { zh: "按 Token 类型的日使用量", en: "Daily usage by token type" },
  "dash.tokenInput": { zh: "输入 Tokens", en: "Input Tokens" },
  "dash.tokenOutput": { zh: "输出 Tokens", en: "Output Tokens" },
  "dash.tokenCacheRead": { zh: "缓存读取 Tokens", en: "Cache Read Tokens" },
  "dash.tokenCacheWrite": { zh: "缓存写入 Tokens", en: "Cache Write Tokens" },
  "dash.recentCalls": { zh: "最近 API 调用", en: "Recent API Calls" },
  "dash.viewAll": { zh: "查看全部", en: "View all" },
  "dash.activeKeys": { zh: "活跃 API Key", en: "Active API Keys" },
  "dash.manage": { zh: "管理", en: "Manage" },
  "dash.quickAccess": { zh: "快速访问", en: "Quick Access" },
  "dash.usageAlert": { zh: "用量提醒", en: "Usage Alert" },
  "dash.usageAlertMsg": { zh: "本月用量已达到软限额的 68%。请考虑升级您的方案。", en: "Your monthly usage is at 68% of the soft limit. Consider upgrading your plan." },
  "dash.voucherBalance": { zh: "代金券余额", en: "Voucher Balance" },
  "dash.billingPlan": { zh: "当前商务方案", en: "Current Billing Plan" },
  // ─── Team/Admin dashboards ───
  "dash.teamTitle": { zh: "团队仪表盘", en: "Team Dashboard" },
  "dash.teamSubtitle": { zh: "团队信息与用量概览", en: "Team info & usage overview" },
  "dash.adminTitle": { zh: "管理员仪表盘", en: "Admin Dashboard" },
  "dash.adminSubtitle": { zh: "全平台用量分析", en: "Platform usage analytics" },
  "dash.metricAmount": { zh: "区间消耗", en: "Period Spend" },
  "dash.metricCalls": { zh: "调用次数", en: "API Calls" },
  "dash.metricSuccessRate": { zh: "成功率", en: "Success Rate" },
  "dash.activeMembers": { zh: "活跃成员", en: "Active Members" },
  "dash.activeTeams": { zh: "活跃团队", en: "Active Teams" },
  "dash.groups": { zh: "分组数", en: "Groups" },
  "dash.usageDisclaimer": { zh: "以上为统计值，可能因时间窗口、当天数据延迟等因素存在误差；实际费用以正式账单为准。", en: "These are statistical values and may vary due to time windows or same-day data lag; actual charges are based on the official bill." },
  "dash.trendTitle": { zh: "用量趋势", en: "Usage Trend" },
  "dash.metricTokens": { zh: "Token", en: "Tokens" },
  "dash.chartLine": { zh: "折线", en: "Line" },
  "dash.chartBar": { zh: "柱状", en: "Bar" },
  "dash.seriesInput": { zh: "输入", en: "Input" },
  "dash.seriesOutput": { zh: "输出", en: "Output" },
  "dash.seriesCacheRead": { zh: "缓存读取", en: "Cache Read" },
  "dash.seriesCacheWrite": { zh: "缓存写入", en: "Cache Write" },
  "dash.seriesAmount": { zh: "金额", en: "Amount" },
  "dash.breakdownTitle": { zh: "维度分布", en: "Breakdown" },
  "dash.loading": { zh: "加载中…", en: "Loading…" },
  "dash.noData": { zh: "暂无数据", en: "No data" },
  "dash.colInput": { zh: "输入", en: "Input" },
  "dash.colOutput": { zh: "输出", en: "Output" },
  "dash.colCacheRead": { zh: "缓存读取", en: "Cache Read" },
  "dash.colCacheWrite": { zh: "缓存写入", en: "Cache Write" },
  "dash.colAmount": { zh: "金额", en: "Amount" },
  "dash.colCalls": { zh: "调用", en: "Calls" },
  "dash.colErrors": { zh: "错误", en: "Errors" },
  "dash.dimByModel": { zh: "按模型", en: "By Model" },
  "dash.dimByApiKey": { zh: "按 API Key", en: "By API Key" },
  "dash.dimByMember": { zh: "按成员", en: "By Member" },
  "dash.dimByTeam": { zh: "按团队", en: "By Team" },
  "dash.globalScope": { zh: "全局（所有团队）", en: "Global (all teams)" },
  "dash.dateRangeError": { zh: "开始日期不能晚于结束日期", en: "Start date cannot be after end date" },
  "dash.dateRangeTooLong": { zh: "时间区间不能超过 366 天", en: "Date range cannot exceed 366 days" },
};

// ─── API Keys ───
const apiKeys: TranslationMap = {
  "keys.title": { zh: "API 密钥", en: "API Keys" },
  "keys.subtitle": { zh: "管理你的 API 密钥和访问凭证", en: "Manage your API keys and access credentials" },
  "keys.newKey": { zh: "新建密钥", en: "New Key" },
  "keys.security": { zh: "API Key 安全", en: "API Key Security" },
  "keys.securityDesc": {
    zh: "您的 API 密钥具有重要权限。切勿在客户端代码、公共仓库或日志中暴露。使用环境变量和服务端代码来保护密钥安全。",
    en: "Your API keys carry privileges. Never expose them in client-side code, public repositories, or logs. Use environment variables and server-side code to keep them secure.",
  },
  "keys.yourKeys": { zh: "你的 API Key", en: "Your API Keys" },
  "keys.created": { zh: "创建于", en: "Created" },
  "keys.lastUsed": { zh: "最后使用", en: "Last used" },
  "keys.monthlyUsage": { zh: "本月用量", en: "Monthly usage" },
  "keys.usingKey": { zh: "使用你的 API Key", en: "Using Your API Key" },
  "keys.createTitle": { zh: "创建新 API Key", en: "Create New API Key" },
  "keys.createdTitle": { zh: "API Key 已创建", en: "API Key Created" },
  "keys.copyNow": { zh: "复制你的密钥（之后也可以在列表里随时查看与复制）", en: "Copy your key now (you can also reveal and copy it later from the list)" },
  "keys.done": { zh: "完成", en: "Done" },
  "keys.keyName": { zh: "密钥名称", en: "Key Name" },
  "keys.keyNamePlaceholder": { zh: "例如：生产环境 API Key", en: "e.g., Production API Key" },
  "keys.monthlyLimit": { zh: "每月使用限额", en: "Monthly Usage Limit" },
  "keys.noLimit": { zh: "无限额 — 使用账户全部余额", en: "No limit — use your full account balance" },
  "keys.securityWarning": { zh: "请妥善保管你的 API Key。切勿在公共仓库或客户端代码中分享。", en: "Keep your API key secure. Never share it in public repositories or client-side code." },
  "keys.cancel": { zh: "取消", en: "Cancel" },
  "keys.create": { zh: "创建密钥", en: "Create Key" },
  "keys.reveal": { zh: "显示完整密钥", en: "Reveal full key" },
  "keys.hide": { zh: "隐藏密钥", en: "Hide key" },
  "keys.copyKey": { zh: "复制密钥", en: "Copy key" },
  "keys.copied": { zh: "已复制到剪贴板", en: "Copied to clipboard" },
  "keys.revealFailed": { zh: "获取完整密钥失败", en: "Failed to reveal key" },
  "keys.legacy": { zh: "legacy", en: "legacy" },
  "keys.legacyTooltip": {
    zh: "此密钥在加密落盘之前创建，无法显示完整内容。撤销后重新创建即可启用查看与复制。",
    en: "This key was created before encryption was enabled and cannot be revealed. Revoke and recreate to enable reveal/copy.",
  },
  "keys.statusActive": { zh: "活跃", en: "active" },
  "keys.statusRevoked": { zh: "已撤销", en: "revoked" },
  "keys.statusExpired": { zh: "已过期", en: "expired" },
  // Expiration time options
  "keys.expirationTime": { zh: "过期时间", en: "Expiration Time" },
  "keys.expNever": { zh: "永不过期", en: "Never Expire" },
  "keys.exp1h": { zh: "1 小时", en: "1 Hour" },
  "keys.exp24h": { zh: "24 小时", en: "24 Hours" },
  "keys.exp7d": { zh: "7 天", en: "7 Days" },
  "keys.exp30d": { zh: "30 天", en: "30 Days" },
  "keys.exp90d": { zh: "90 天", en: "90 Days" },
  "keys.exp1y": { zh: "1 年", en: "1 Year" },
  // Toast / action messages
  "keys.copyFailed": { zh: "复制失败", en: "Copy failed" },
  "keys.createFailed": { zh: "创建密钥失败", en: "Failed to create key" },
  "keys.loadFailed": { zh: "加载 API 密钥失败", en: "Failed to load API keys" },
  "logs.loadFailed": { zh: "加载失败", en: "Failed to load" },
  "admin.bills.previewFailed": { zh: "预览失败", en: "Preview failed" },
  "admin.bills.operationFailed": { zh: "操作失败", en: "Operation failed" },
  "keys.revokeSuccess": { zh: "API 密钥已撤销", en: "API key revoked" },
  "keys.revokeFailed": { zh: "撤销密钥失败", en: "Failed to revoke key" },
  "keys.creating": { zh: "创建中...", en: "Creating..." },
  "keys.revokeKey": { zh: "撤销", en: "Revoke" },
  "keys.revokeConfirmTitle": { zh: "撤销 API Key", en: "Revoke API Key" },
  "keys.revokeConfirmDesc": {
    zh: "撤销后该密钥将立即失效且不可恢复，使用它的调用会全部失败。确认撤销吗？",
    en: "Once revoked, this key stops working immediately and cannot be restored; any calls using it will fail. Revoke it?",
  },
  "keys.emptyState": { zh: "暂无 API 密钥。创建一个开始使用。", en: "No API keys yet. Create one to get started." },
  // Code block headers
  "keys.codeEnvVar": { zh: "环境变量", en: "Environment Variable" },
  "keys.codeCurl": { zh: "cURL", en: "cURL" },
  "keys.inactiveSection": { zh: "已失效的密钥", en: "Inactive keys" },
  "keys.protoAnthropic": { zh: "Anthropic", en: "Anthropic" },
  "keys.protoOpenAI": { zh: "OpenAI", en: "OpenAI" },
};

// ─── Billing ───
const billing: TranslationMap = {
  "billing.title": { zh: "计费与充值", en: "Billing & Credits" },
  "billing.subtitle": { zh: "管理您的 ScienceToken 额度和计费历史", en: "Manage your ScienceToken credits and billing history" },
  "billing.topUp": { zh: "充值", en: "Top Up Credits" },
  "billing.balance": { zh: "可用余额", en: "Available Balance" },
  "billing.monthlyBudget": { zh: "月度预算", en: "of monthly budget" },
  "billing.thisMonth": { zh: "本月消费", en: "This Month Spent" },
  "billing.vsLastMonth": { zh: "较上月", en: "vs last month" },
  "billing.byService": { zh: "按服务分布", en: "Spending by Service" },
  "billing.monthlyTrend": { zh: "月度消费趋势 (USD)", en: "Monthly Spending Trend (USD)" },
  "billing.transactions": { zh: "交易记录", en: "Transaction History" },
  "billing.export": { zh: "导出", en: "Export" },
  "billing.selectAmount": { zh: "选择金额 (USD)", en: "Select Amount (USD)" },
  "billing.change": { zh: "更换", en: "Change" },
  "billing.pay": { zh: "支付", en: "Pay" },

  // Shared discount/cycle/voucher copy — reused by Dashboard, TeamManagement
  // and AdminTeams so the wording stays consistent across all three views.
  "billing.voucherBalance": { zh: "代金券余额", en: "Voucher Balance" },
  "billing.discountLabel": { zh: "当前商务折扣", en: "Current Discount" },
  "billing.cycleLabel": { zh: "出账周期", en: "Billing Cycle" },
  "billing.noDiscount": { zh: "无折扣 / 100%", en: "No discount (100%)" },
  "billing.discountUnit": { zh: "折", en: "off" },
  "billing.cycleMonthly": { zh: "月度（自然月，上月1号→本月1号）", en: "Monthly (1st → 1st)" },
  "billing.cycleWeekly": { zh: "周（周一~周日）", en: "Weekly (Mon–Sun)" },
  "billing.viewConfig": { zh: "查看计费配置", en: "View billing config" },

  // Task 6: voucher recharge/adjustment + editable billing config modals
  "billing.voucherRecharge": { zh: "代金券充值", en: "Voucher Recharge" },
  "billing.modalVoucherRechargeTitle": { zh: "代金券充值 / 调整", en: "Voucher Recharge / Adjustment" },
  "billing.voucherAmountLabel": { zh: "金额 (USD)", en: "Amount (USD)" },
  "billing.voucherAmountHint": { zh: "正数为充值，负数为调整/回收；不允许为 0。", en: "Positive to top up, negative to adjust/recall. Zero is not allowed." },
  "billing.voucherAmountInvalid": { zh: "请输入非零的有效金额", en: "Enter a valid non-zero amount" },
  "billing.voucherRechargedToast": { zh: "代金券余额已更新", en: "Voucher balance updated" },
  "billing.currentVoucherBalance": { zh: "当前代金券余额", en: "Current voucher balance" },
  "billing.discountRateHint": { zh: "范围 (0, 1]，如 0.9 表示 9 折", en: "Range (0, 1], e.g. 0.9 means a 10% discount" },
  "billing.discountRateInvalid": { zh: "折扣率必须在 (0, 1] 之间", en: "Discount rate must be in (0, 1]" },
  "billing.configUpdatedToast": { zh: "计费配置已更新", en: "Billing config updated" },
};

// ─── Settings ───
const settings: TranslationMap = {
  "settings.title": { zh: "设置", en: "Settings" },
  "settings.subtitle": { zh: "管理您的账户和偏好", en: "Manage your account and preferences" },
  "settings.profile": { zh: "个人资料", en: "Profile" },
  "settings.notifications": { zh: "通知", en: "Notifications" },
  "settings.security": { zh: "安全", en: "Security" },
  "settings.preferences": { zh: "偏好设置", en: "Preferences" },
  "settings.personalInfo": { zh: "个人信息", en: "Personal Information" },
  "settings.fullName": { zh: "姓名", en: "Full Name" },
  "settings.email": { zh: "邮箱", en: "Email" },
  "settings.organization": { zh: "组织", en: "Organization" },
  "settings.roleTitle": { zh: "角色 / 职称", en: "Role / Title" },
  "settings.save": { zh: "保存更改", en: "Save Changes" },
  "settings.changeAvatar": { zh: "更换头像", en: "Change Avatar" },
  "settings.notifPrefs": { zh: "通知偏好", en: "Notification Preferences" },
  "settings.usageAlerts": { zh: "用量提醒", en: "Usage Alerts" },
  "settings.usageAlertsDesc": { zh: "当用量超过月度限额 80% 时通知", en: "Notify when usage exceeds 80% of monthly limit" },
  "settings.jobComplete": { zh: "任务完成", en: "Job Completion" },
  "settings.jobCompleteDesc": { zh: "计算任务完成时通知", en: "Notify when compute jobs finish" },
  "settings.weeklyReport": { zh: "周报", en: "Weekly Report" },
  "settings.weeklyReportDesc": { zh: "接收每周使用和费用摘要", en: "Receive weekly usage and cost summary" },
  "settings.securityAlerts": { zh: "安全提醒", en: "Security Alerts" },
  "settings.securityAlertsDesc": { zh: "新登录和 API Key 使用时通知", en: "Notify on new logins and API key usage" },
  "settings.productUpdates": { zh: "产品更新", en: "Product Updates" },
  "settings.productUpdatesDesc": { zh: "新模型、功能和公告", en: "New models, features, and announcements" },
  "settings.teamActivity": { zh: "团队活动", en: "Team Activity" },
  "settings.teamActivityDesc": { zh: "团队成员邀请和角色变更时通知", en: "Notify on team member invites and role changes" },
  "settings.password": { zh: "密码", en: "Password" },
  "settings.currentPassword": { zh: "当前密码", en: "Current Password" },
  "settings.newPassword": { zh: "新密码", en: "New Password" },
  "settings.confirmPassword": { zh: "确认新密码", en: "Confirm New Password" },
  "settings.updatePassword": { zh: "更新密码", en: "Update Password" },
  "settings.pwdAllRequired": { zh: "请填写所有字段", en: "Please fill in all fields" },
  "settings.pwdTooShort": { zh: "新密码至少 8 位", en: "New password must be at least 8 characters" },
  "settings.pwdSameAsOld": { zh: "新密码必须不同于旧密码", en: "New password must differ from current" },
  "settings.pwdMismatch": { zh: "两次输入的新密码不一致", en: "Passwords do not match" },
  "settings.pwdUpdated": { zh: "密码已更新", en: "Password updated" },
  "settings.twoFactor": { zh: "双因素认证", en: "Two-Factor Authentication" },
  "settings.twoFactorDesc": { zh: "为您的账户添加额外的安全保护。", en: "Add an extra layer of security to your account." },
  "settings.authenticator": { zh: "认证器应用", en: "Authenticator App" },
  "settings.notConfigured": { zh: "未配置", en: "Not configured" },
  "settings.enable": { zh: "启用", en: "Enable" },
  "settings.activeSessions": { zh: "活跃会话", en: "Active Sessions" },
  "settings.current": { zh: "当前", en: "Current" },
  "settings.revoke": { zh: "撤销", en: "Revoke" },
  "settings.language": { zh: "语言", en: "Language" },
  "settings.timezone": { zh: "时区", en: "Timezone" },
  "settings.defaultModel": { zh: "默认 LLM 模型", en: "Default LLM Model" },
  "settings.platformTheme": { zh: "平台主题", en: "Platform Theme" },
  "settings.themeTech": { zh: "科技风", en: "Tech" },
  "settings.themeTechDesc": { zh: "深色背景，科技感强", en: "Dark background, technology-focused" },
  "settings.themeFresh": { zh: "清新风", en: "Fresh" },
  "settings.themeFreshDesc": { zh: "浅色背景，简洁清新", en: "Light background, clean and minimal" },
};

// ─── Team ───
const team: TranslationMap = {
  "team.title": { zh: "团队", en: "Team" },
  "team.subtitle": { zh: "管理团队成员和访问权限", en: "Manage team members and access permissions" },
  "team.invite": { zh: "邀请成员", en: "Invite Member" },
  "team.totalMembers": { zh: "总成员", en: "Total Members" },
  "team.admins": { zh: "管理员", en: "Admins" },
  "team.activeToday": { zh: "今日活跃", en: "Active Today" },
  "team.pendingInvites": { zh: "待处理邀请", en: "Pending Invites" },
  "team.members": { zh: "成员", en: "Members" },
  "team.thisMonth": { zh: "本月", en: "this month" },
  "team.rolePermissions": { zh: "角色权限", en: "Role Permissions" },
  "team.permission": { zh: "权限", en: "Permission" },
  "team.manageApiKeys": { zh: "管理 API Key", en: "Manage API Keys" },
  "team.viewUsage": { zh: "查看用量和计费", en: "View Usage & Billing" },
  "team.callLLM": { zh: "调用 LLM API", en: "Call LLM APIs" },
  "team.submitCompute": { zh: "提交计算任务", en: "Submit Compute Jobs" },
  "team.queryDb": { zh: "查询数据库", en: "Query Databases" },
  "team.useSciNexus": { zh: "使用 SciNexus", en: "Use SciNexus" },
  "team.inviteMembers": { zh: "邀请成员", en: "Invite Members" },
  "team.manageBilling": { zh: "管理计费", en: "Manage Billing" },
  "team.inviteTitle": { zh: "邀请团队成员", en: "Invite Team Member" },
  "team.emailAddress": { zh: "邮箱地址", en: "Email Address" },
  "team.role": { zh: "角色", en: "Role" },
  "team.adminDesc": { zh: "除计费外的完全访问权限", en: "Full access except billing" },
  "team.memberDesc": { zh: "可调用 API 和使用 SciNexus", en: "Can call APIs and use SciNexus" },
  "team.viewerDesc": { zh: "只读访问权限", en: "Read-only access" },
  "team.cancel": { zh: "取消", en: "Cancel" },
  "team.sendInvite": { zh: "发送邀请", en: "Send Invite" },
};

// ─── Team Management (team_admin self-service) ───
const teamMgmt: TranslationMap = {
  "teamMgmt.title": { zh: "团队成员管理", en: "Member Management" },
  "teamMgmt.subtitle": { zh: "管理团队成员、账号状态与成员操作", en: "Manage team members, account status, and member actions" },
  "teamMgmt.invite": { zh: "邀请成员", en: "Invite Member" },
  "teamMgmt.inviteTitle": { zh: "邀请新成员", en: "Invite New Member" },
  "teamMgmt.inviteHint": { zh: "通过邮箱添加新成员到当前团队，角色为普通成员。系统将自动生成初始密码（仅显示一次）。", en: "Add a new member to this team by email. The role is set to regular member. An initial password will be generated and shown once." },
  "teamMgmt.teamInfo": { zh: "团队信息", en: "Team Info" },
  "teamMgmt.totalMembers": { zh: "总成员", en: "Total" },
  "teamMgmt.activeMembers": { zh: "活跃", en: "Active" },
  "teamMgmt.admins": { zh: "管理员", en: "Admins" },
  "teamMgmt.created": { zh: "创建于", en: "Created" },
  "teamMgmt.wallet": { zh: "钱包余额", en: "Wallet" },
  "teamMgmt.walletHint": { zh: "由系统管理员充值。如需充值请联系系统管理员。", en: "Recharged by the system admin. Contact your sys admin to top up." },
  "teamMgmt.quota": { zh: "配额", en: "Quota" },
  "teamMgmt.tokenLimit": { zh: "TPM", en: "TPM" },
  "teamMgmt.requestLimit": { zh: "RPM", en: "RPM" },
  "teamMgmt.memberLimit": { zh: "人员上限", en: "Member Limit" },
  "teamMgmt.unlimited": { zh: "无限", en: "Unlimited" },
  "teamMgmt.quotaHint": { zh: "由系统管理员设置。", en: "Set by the system admin." },
  "teamMgmt.members": { zh: "团队成员", en: "Members" },
  "teamMgmt.noMembers": { zh: "暂无成员", en: "No members" },
  "teamMgmt.colUser": { zh: "用户", en: "User" },
  "teamMgmt.colEmail": { zh: "邮箱", en: "Email" },
  "teamMgmt.colRole": { zh: "角色", en: "Role" },
  "teamMgmt.roleAdmin": { zh: "Admin", en: "Admin" },
  "teamMgmt.roleMember": { zh: "Member", en: "Member" },
  "teamMgmt.colStatus": { zh: "状态", en: "Status" },
  "teamMgmt.statusActive": { zh: "已激活", en: "Active" },
  "teamMgmt.statusInactive": { zh: "已停用", en: "Inactive" },
  "teamMgmt.colActions": { zh: "操作", en: "Actions" },
  "teamMgmt.lastLogin": { zh: "最近登录", en: "Last login" },
  "teamMgmt.activate": { zh: "激活", en: "Activate" },
  "teamMgmt.deactivate": { zh: "停用", en: "Deactivate" },
  "teamMgmt.activated": { zh: "已激活", en: "activated" },
  "teamMgmt.deactivated": { zh: "已停用", en: "deactivated" },
  "teamMgmt.cannotSelf": { zh: "不能停用自己", en: "Cannot deactivate yourself" },
  "teamMgmt.cannotSysadmin": { zh: "不能停用系统管理员", en: "Cannot deactivate system admin" },
  "teamMgmt.emailLabel": { zh: "邮箱", en: "Email" },
  "teamMgmt.displayNameLabel": { zh: "显示名称", en: "Display Name" },
  "teamMgmt.optional": { zh: "(可选)", en: "(optional)" },
  "teamMgmt.emailRequired": { zh: "请输入邮箱", en: "Email is required" },
  "teamMgmt.emailExists": { zh: "该邮箱已被注册", en: "Email already registered" },
  "teamMgmt.pwdOnceWarn": { zh: "初始密码仅显示一次，请立即复制并安全发送给该用户。", en: "This password is shown only once. Copy it now and send it to the user securely." },
  "teamMgmt.mustChangePwd": { zh: "用户首次登录时将被强制修改密码。", en: "The user must change this password on first login." },
  "teamMgmt.cancel": { zh: "取消", en: "Cancel" },
  "teamMgmt.create": { zh: "创建", en: "Create" },
  "teamMgmt.done": { zh: "完成", en: "Done" },
  "teamMgmt.resetPwd": { zh: "重置", en: "Reset" },
  "teamMgmt.resetPwdConfirmTitle": { zh: "重置成员密码", en: "Reset member password" },
  "teamMgmt.resetPwdConfirmBody": { zh: "确认为下方成员生成新的初始密码？该用户当前密码将立即失效。", en: "Generate a new initial password for the member below? Their current password will stop working immediately." },
  "teamMgmt.resetPwdConfirm": { zh: "确认重置", en: "Confirm reset" },
  "teamMgmt.resetPwdSuccess": { zh: "密码已重置", en: "Password reset" },
  "teamMgmt.resetPwdFailed": { zh: "重置密码失败", en: "Reset password failed" },
  "teamMgmt.selfTag": { zh: "你", en: "you" },

  "teamMgmt.promoteSysadmin": { zh: "设为管理员", en: "Make Admin" },
  "teamMgmt.demoteSysadmin": { zh: "取消管理员", en: "Remove Admin" },
  "teamMgmt.promoteSysadminTitle": { zh: "提升为系统管理员", en: "Promote to system admin" },
  "teamMgmt.demoteSysadminTitle": { zh: "取消系统管理员", en: "Demote from system admin" },
  "teamMgmt.demoteSysadminConfirm": { zh: "确定要取消 {name} ({email}) 的系统管理员权限吗？", en: "Revoke system admin from {name} ({email})?" },
  "teamMgmt.promoteSysadminConfirm": { zh: "确定要将 {name} ({email}) 设置为系统管理员吗？系统管理员拥有平台的最高权限。", en: "Grant system admin to {name} ({email})? System admins have full platform privileges." },
  "teamMgmt.promoteSysadminSuccess": { zh: "已提升为系统管理员", en: "Promoted to system admin" },
  "teamMgmt.demoteSysadminSuccess": { zh: "已取消系统管理员", en: "Demoted from system admin" },
  "teamMgmt.cannotDemoteSelf": { zh: "不能取消自己的系统管理员权限", en: "Cannot demote yourself from system admin" },
  "teamMgmt.deactivateConfirmTitle": { zh: "确认停用成员", en: "Confirm Deactivation" },
  "teamMgmt.deactivateWarn": { zh: "停用后该成员将无法访问，直到被重新激活。", en: "Once deactivated, this member will lose access until reactivated." },
  "teamMgmt.deactivateSysadminWarn": { zh: "该成员是系统管理员。停用后将无法访问，直到被重新激活。", en: "This member is a system admin. They will lose all access until reactivated." },
  "teamMgmt.deletePermanent": { zh: "彻底删除", en: "Delete Permanently" },
  "teamMgmt.deleteConfirmTitle": { zh: "确认彻底删除成员", en: "Confirm Permanent Deletion" },
  "teamMgmt.deleteWarn": { zh: "此操作将永久删除该成员及其所有 API 密钥，不可撤销。使用记录将保留但用户信息会被匿名化。", en: "This will PERMANENTLY delete the member and all their API keys. This action cannot be undone. Usage history will be preserved with the user anonymized." },
  "teamMgmt.deleteSuccess": { zh: "成员已彻底删除", en: "Member permanently deleted" },
  "teamMgmt.deleteFailed": { zh: "彻底删除失败", en: "Permanent deletion failed" },
  "teamMgmt.updateFailed": { zh: "更新失败", en: "Update failed" },

  // Read-only credit/billing overview (team_admin view; all values come from
  // the team wallet API — no edit controls here, that's sys_admin-only).
  "teamMgmt.creditLimit": { zh: "信用额度", en: "Credit Limit" },
  "teamMgmt.creditUsed": { zh: "已用信用", en: "Credit Used" },
  "teamMgmt.available": { zh: "剩余可用", en: "Available" },
  "teamMgmt.debt": { zh: "欠款", en: "Debt" },
  "teamMgmt.creditHint": { zh: "信用额度由系统管理员设置，欠款需由系统管理员结算。", en: "Set by the system admin. Outstanding debt is settled by the system admin." },
  "teamMgmt.bills": { zh: "账单记录", en: "Bills" },
  "teamMgmt.billsHint": { zh: "只读；出账与结算由系统管理员操作。", en: "Read-only; billing and settlement are performed by the system admin." },
  "teamMgmt.colPeriod": { zh: "账期", en: "Period" },
  "teamMgmt.colAmountDue": { zh: "应付额", en: "Amount Due" },
  "teamMgmt.colPaidAt": { zh: "结清时间", en: "Paid At" },
  "teamMgmt.billStatusUnpaid": { zh: "未结清", en: "Unpaid" },
  "teamMgmt.billStatusPaid": { zh: "已结清", en: "Paid" },
  "teamMgmt.billStatusVoid": { zh: "已作废", en: "Void" },
  "teamMgmt.noBills": { zh: "暂无账单", en: "No bills" },
};

// ─── Team Bills (team_admin read-only billing view, split from TeamManagement) ───
const teamBills: TranslationMap = {
  "teamBills.title": { zh: "团队账单记录", en: "Team Bills" },
  "teamBills.subtitle": { zh: "查看本团队历史出账账单，出账与结算由系统管理员操作", en: "Review your team's issued bills; billing and settlement are handled by the system admin" },
  "teamBills.walletInfo": { zh: "团队钱包信息", en: "Team Wallet" },
  "teamBills.cashBalance": { zh: "预存余额", en: "Prepaid Balance" },
  "teamBills.creditAvailable": { zh: "可用信用", en: "Available Credit" },
  "teamBills.voucher": { zh: "代金券", en: "Voucher" },
  "teamBills.debt": { zh: "欠款", en: "Outstanding Debt" },
  "teamBills.walletHint": { zh: "预存余额与代金券由系统管理员充值；欠款需由系统管理员结算。", en: "Prepaid balance and vouchers are recharged by the system admin; outstanding debt is settled by the system admin." },
  "teamBills.bills": { zh: "账单列表", en: "Bills" },
  "teamBills.billsHint": { zh: "只读；出账与结算由系统管理员操作。", en: "Read-only; billing and settlement are performed by the system admin." },
  "teamBills.colPeriod": { zh: "账期", en: "Period" },
  "teamBills.colTotal": { zh: "总消耗", en: "Total Consumption" },
  "teamBills.colAmountDue": { zh: "应付金额", en: "Amount Due" },
  "teamBills.colStatus": { zh: "状态", en: "Status" },
  "teamBills.colActions": { zh: "操作", en: "Actions" },
  "teamBills.viewBill": { zh: "查看账单", en: "View Bill" },
  "teamBills.exportBill": { zh: "导出对账单", en: "Export Statement" },
  "teamBills.statusUnpaid": { zh: "未结清", en: "Unpaid" },
  "teamBills.statusPaid": { zh: "已结清", en: "Paid" },
  "teamBills.statusVoid": { zh: "已作废", en: "Void" },
  "teamBills.noBills": { zh: "暂无账单", en: "No bills yet" },
  "teamBills.resetFilter": { zh: "重置筛选", en: "Reset Filters" },
  "teamBills.loading": { zh: "加载中…", en: "Loading…" },
  "teamBills.loadFailed": { zh: "加载失败", en: "Failed to load" },
  "teamBills.detailLoadFailed": { zh: "加载账单详情失败", en: "Failed to load bill detail" },
  "teamBills.exportFailed": { zh: "导出失败", en: "Export failed" },
  "teamBills.billDetail": { zh: "账单详情", en: "Bill Detail" },
};

// ─── Bill PDF export ───
const billPdf: TranslationMap = {
  "billPdf.previewBill": { zh: "账单预览", en: "Bill Preview" },
  "billPdf.previewPdf": { zh: "预览 PDF", en: "Preview PDF" },
  "billPdf.downloadPdf": { zh: "下载 PDF", en: "Download PDF" },
  "billPdf.print": { zh: "打印", en: "Print" },
  "billPdf.close": { zh: "关闭", en: "Close" },
  "billPdf.previewTitleSuffix": { zh: "账单预览", en: "Bill Preview" },
  "billPdf.selectBill": { zh: "选择账单", en: "Select bill" },
  "billPdf.selectAllPage": { zh: "全选本页", en: "Select all on this page" },
  "billPdf.batchDownload": { zh: "批量下载", en: "Batch Download" },
  "billPdf.batchFetching": { zh: "正在获取账单...", en: "Fetching bills..." },
  "billPdf.batchGenerating": { zh: "正在生成账单 PDF...", en: "Generating bill PDFs..." },
  "billPdf.batchSuccess": { zh: "批量下载已开始", en: "Batch download started" },
  "billPdf.failuresTitle": { zh: "导出失败明细", en: "Export failures" },
  "billPdf.failuresIntro": { zh: "以下账单未能导出，其余账单已包含在 ZIP 中：", en: "The following bills could not be exported; the others are included in the ZIP:" },
  "billPdf.failuresAllIntro": { zh: "以下账单均未能导出：", en: "The following bills all failed to export:" },
  "billPdf.failureReason": { zh: "失败原因", en: "Reason" },
  "billPdf.resultClose": { zh: "知道了", en: "OK" },
  "billPdf.downloadFailed": { zh: "PDF 下载失败", en: "Failed to download PDF" },
  "billPdf.downloadStarted": { zh: "PDF 下载已开始", en: "PDF download started" },
};

// ─── Experiment Logs ───
const logs: TranslationMap = {
  "logs.title": { zh: "调用日志", en: "Call Logs" },
  "logs.subtitle": { zh: "所有 API 调用和计算任务的详细记录", en: "Detailed records of all API calls and compute jobs" },
  "logs.exportCsv": { zh: "导出 CSV", en: "Export CSV" },
  "logs.search": { zh: "按模型或请求 ID 搜索...", en: "Search by model or request ID..." },
  "logs.searchByModel": { zh: "ScienceLLM 模型", en: "ScienceLLM Model" },
  "logs.searchByRequestId": { zh: "请求 ID", en: "Request ID" },
  "logs.searchModelPlaceholder": { zh: "如 xtalpi-science-standard", en: "e.g. xtalpi-science-standard" },
  "logs.searchRequestIdPlaceholder": { zh: "输入请求 ID...", en: "Enter request ID..." },
  "logs.applyFilters": { zh: "查询", en: "Apply" },
  "logs.statusAll": { zh: "全部状态", en: "All Status" },
  "logs.statusSuccess": { zh: "成功", en: "Success" },
  "logs.statusFailed": { zh: "失败", en: "Failed" },
  "logs.statusRunning": { zh: "进行中", en: "Running" },
  "logs.records": { zh: "条记录", en: "records" },
  "logs.noLogs": { zh: "没有匹配的日志。", en: "No logs match your filters." },
  "logs.modelRequest": { zh: "模型 / 请求", en: "Model / Request" },
  "logs.usage": { zh: "使用量", en: "Usage" },
  "logs.cost": { zh: "费用", en: "Cost" },

  // Billing v2: per-request funding source badge + list/discount breakdown
  "logs.fundingVoucher": { zh: "代金券支付", en: "Voucher" },
  "logs.fundingCash": { zh: "现金支付", en: "Cash" },
  "logs.fundingCredit": { zh: "信用支付", en: "Credit" },
  "logs.listAmount": { zh: "原价", en: "List Price" },
  "logs.discountRate": { zh: "折扣", en: "Discount" },
  "logs.actualAmount": { zh: "折后金额", en: "Actual Amount" },

  // Usage detail fields (expanded row)
  "logs.detail.requestId": { zh: "请求 ID", en: "Request ID" },
  "logs.detail.model": { zh: "模型", en: "Model" },
  "logs.detail.upstream": { zh: "上游模型", en: "Upstream" },
  "logs.detail.provider": { zh: "服务商", en: "Provider" },
  "logs.detail.status": { zh: "状态", en: "Status" },
  "logs.detail.latency": { zh: "延迟", en: "Latency" },
  "logs.detail.inputTokens": { zh: "输入 Tokens", en: "Input Tokens" },
  "logs.detail.outputTokens": { zh: "输出 Tokens", en: "Output Tokens" },
  "logs.detail.cacheReadTokens": { zh: "缓存读 Tokens", en: "Cache Read Tokens" },
  "logs.detail.cacheWriteTokens": { zh: "缓存写 Tokens", en: "Cache Write Tokens" },
  "logs.detail.pricingTier": { zh: "阶梯档位", en: "Pricing Tier" },
  "logs.detail.unitPriceInput": { zh: "单价 输入", en: "Unit Price Input" },
  "logs.detail.unitPriceOutput": { zh: "单价 输出", en: "Unit Price Output" },
  "logs.detail.unitPriceCacheRead": { zh: "单价 缓存读", en: "Unit Price Cache Read" },
  "logs.detail.unitPriceCacheWrite": { zh: "单价 缓存写", en: "Unit Price Cache Write" },
  "logs.detail.apiKey": { zh: "API Key", en: "API Key" },

  // Team usage view (team_admin scope)
  "logs.team.title": { zh: "团队调用日志", en: "Team Call Logs" },
  "logs.team.subtitle": { zh: "团队内所有成员的 API 调用记录（仅团队管理员可见）", en: "API call records for every member of your team (team admin only)" },
  "logs.team.member": { zh: "成员", en: "Member" },
  "logs.team.allMembers": { zh: "全部成员", en: "All members" },
  "logs.team.unknownUser": { zh: "未知用户", en: "Unknown user" },
  "logs.team.avgAttempts": { zh: "平均尝试次数", en: "Avg Attempts" },
  "logs.team.retryRate": { zh: "重试率", en: "Retry Rate" },
  "logs.team.terminalCount": { zh: "放弃重试数", en: "Terminal Count" },
  "logs.team.budgetExceededCount": { zh: "重试超限数", en: "Budget Exceeded" },

  // Admin cross-team usage view (system_admin scope)
  "logs.admin.title": { zh: "全部调用日志", en: "All Call Logs" },
  "logs.admin.subtitle": { zh: "跨团队的所有 API 调用记录，含上游模型与服务商详情", en: "Cross-team API call records, including upstream model and provider details" },
  "logs.admin.attemptsTitle": { zh: "选址尝试日志", en: "Usage Attempts" },
  "logs.admin.attemptsSubtitle": { zh: "查看并调试纯随机选址的重试链路与底层错误", en: "View and debug random failover attempt chains and upstream errors" },
  "logs.admin.errorMax7Days": { zh: "查询时间范围不可超过 7 天", en: "Date range cannot exceed 7 days" },

  // Admin margin dashboard (system_admin scope)
  "margin.admin.title": { zh: "毛利预估仪表盘", en: "Margin Dashboard" },
  "margin.admin.subtitle": { zh: "按供应商 / 模型 / 客户查看成本、售价与毛利，并预估当前账期毛利", en: "Cost, revenue and margin by provider / model / customer, with current-cycle forecast" },
  // Margin dashboard 2.0 — filters & dimensions
  "margin.filter.providerLabel": { zh: "供应商：", en: "Provider:" },
  "margin.filter.customerLabel": { zh: "客户：", en: "Customer:" },
  "margin.filter.allProviders": { zh: "全部供应商", en: "All providers" },
  "margin.filter.allCustomers": { zh: "全部客户", en: "All customers" },
  "admin.providers.searchPlaceholder": { zh: "搜索供应商…", en: "Search providers…" },
  "admin.llms.searchPlaceholder": { zh: "搜索模型名…", en: "Search models…" },
  "margin.filter.active": { zh: "筛选中：", en: "Filters:" },
  "margin.dim.provider": { zh: "供应商", en: "Provider" },
  "margin.dim.model": { zh: "模型", en: "Model" },
  "margin.dim.customer": { zh: "客户", en: "Customer" },
  "margin.dim.science": { zh: "对客产品", en: "Product" },
  // KPI cards
  "margin.kpi.revenue": { zh: "总售价（营收）", en: "Total revenue" },
  "margin.kpi.cost": { zh: "总成本", en: "Total cost" },
  "margin.kpi.margin": { zh: "总毛利", en: "Total margin" },
  "margin.kpi.marginRate": { zh: "综合毛利率", en: "Blended margin rate" },
  "margin.kpi.vsPrev": { zh: "环比", en: "vs prev" },
  // Trend
  "margin.trend.title": { zh: "毛利与毛利率走势", en: "Margin & margin-rate trend" },
  "margin.trend.subtitle": { zh: "毛利额 + 毛利率双轴", en: "Margin amount + rate (dual axis)" },
  "margin.trend.byDay": { zh: "按天", en: "Daily" },
  "margin.trend.byWeek": { zh: "按周", en: "Weekly" },
  "margin.trend.marginLine": { zh: "毛利额(USD)", en: "Margin (USD)" },
  "margin.trend.rateLine": { zh: "毛利率(%)", en: "Margin rate (%)" },
  // Shared / table columns
  "margin.loading": { zh: "加载中…", en: "Loading…" },
  "margin.col.count": { zh: "次数", en: "Requests" },
  "margin.col.cost": { zh: "成本(USD)", en: "Cost (USD)" },
  "margin.col.revenue": { zh: "售价(USD)", en: "Revenue (USD)" },
  "margin.col.margin": { zh: "毛利(USD)", en: "Margin (USD)" },
  "margin.col.marginRate": { zh: "毛利率", en: "Margin rate" },
  // Section blocks
  "margin.block.byProvider": { zh: "按供应商 · 毛利率对比", en: "By provider · margin rate" },
  "margin.block.byProviderSub": { zh: "柱状 = 成本 vs 售价(USD) · 客户筛选下即为该客户在各供应商", en: "Bars = cost vs revenue (USD); with a customer filter, shows that customer across providers" },
  "margin.block.byModel": { zh: "按供应商 - 模型", en: "By provider - model" },
  "margin.block.byModelSub": { zh: "供应商 + 模型双列，同名模型不再混淆", en: "Provider + model columns; same-named models no longer conflated" },
  "margin.block.byScience": { zh: "按 ScienceLLM 对客产品", en: "By ScienceLLM product" },
  "margin.block.byScienceSub": { zh: "按用户实际请求的对客产品聚合", en: "Aggregated by the product users actually requested" },
  "margin.block.byCustomer": { zh: "按客户 · 营收与毛利", en: "By customer · revenue & margin" },
  "margin.block.byCustomerSub": { zh: "供应商筛选下即为各客户在该供应商", en: "With a provider filter, shows each customer on that provider" },
  "margin.block.costShare": { zh: "成本占比", en: "Cost share" },
  "margin.block.revenueShare": { zh: "营收占比", en: "Revenue share" },
  // Forecast
  "margin.forecast.title": { zh: "毛利预估（当前账期）", en: "Margin forecast (current cycle)" },
  "margin.forecast.subtitle": { zh: "run-rate 线性外推 · 随客户筛选，不随供应商 · 负毛利标红", en: "Linear run-rate extrapolation · follows customer filter, not provider · negatives in red" },
  "margin.forecast.colProgress": { zh: "账期进度", en: "Cycle progress" },
  "margin.forecast.colRealized": { zh: "已实现毛利(USD)", en: "Realized margin (USD)" },
  "margin.forecast.colProjected": { zh: "预估整期毛利(USD)", en: "Projected margin (USD)" },
  "margin.forecast.negPart1": { zh: "本账期毛利为负", en: "margin is negative this cycle:" },
  "margin.forecast.negPart2": { zh: "，预估整期", en: "; projected full-cycle:" },
  // Insights
  "margin.insight.title": { zh: "洞察", en: "Insights" },
  "margin.insight.topRate": { zh: "毛利率最高", en: "has the highest margin rate" },
  "margin.insight.negMargin": { zh: "毛利为负", en: "margin is negative" },
  "logs.admin.allOutcomes": { zh: "所有结果", en: "All Outcomes" },
  "logs.admin.filterByProvider": { zh: "服务商", en: "Provider Slug" },
  "logs.admin.filterByStatus": { zh: "上游状态码", en: "Upstream Status" },
  "logs.admin.errorUpstreamStatusDigits": { zh: "上游状态码需为数字，请重新输入", en: "Upstream status must be a number" },
  "logs.admin.team": { zh: "团队", en: "Team" },
  "logs.admin.allTeams": { zh: "全部团队", en: "All teams" },
  "logs.admin.user": { zh: "用户", en: "User" },
  "logs.admin.filterByTeam": { zh: "按团队 ID 过滤...", en: "Filter by team ID..." },
  "logs.admin.filterByUser": { zh: "按用户 ID 过滤...", en: "Filter by user ID..." },
};

// ─── Console SciNexus ───
const consoleScinexus: TranslationMap = {
  "cscinexus.welcome": {
    zh: "你好！我是 SciNexus Agent，您的 AI 科研助手。\n\n我可以帮您：\n• 预测蛋白质结构（AlphaFold3、ESMFold）\n• 执行分子对接（DiffDock）\n• 分析分子性质（Chemprop、Uni-Mol）\n• 查询专有科学数据库\n• 生成端到端科研报告\n\n请描述您的科研目标，我将自动规划并执行工作流。",
    en: "Hello! I'm SciNexus Agent, your AI research assistant.\n\nI can help you:\n• Predict protein structures (AlphaFold3, ESMFold)\n• Perform molecular docking (DiffDock)\n• Analyze molecular properties (Chemprop, Uni-Mol)\n• Query proprietary scientific databases\n• Generate end-to-end research reports\n\nDescribe your research goal and I'll plan and execute the workflow automatically.",
  },
};

// ─── Data Descriptions (models, databases, workflows) ───
const dataDesc: TranslationMap = {
  // Science LLMs
  "data.llm.flagship.desc": {
    zh: "晶泰科技自研旗舰科学大语言模型，原生理解 SMILES、FASTA、InChI 等科学格式，支持多模态输入，专为药物发现和材料科学场景优化。",
    en: "XtalPi's proprietary flagship scientific LLM that natively understands SMILES, FASTA, InChI, and other scientific formats. Supports multimodal input, optimized for drug discovery and materials science.",
  },
  "data.llm.deepseek.desc": {
    zh: "基于 DeepSeek-V4 在晶泰科学语料上微调的开源科学 LLM，在分子性质预测和科学问答任务上表现优异，性价比极高。",
    en: "Open-source scientific LLM fine-tuned on XtalPi's scientific corpus based on DeepSeek-V4. Excels in molecular property prediction and scientific QA tasks with exceptional cost-effectiveness.",
  },
  "data.llm.qwen.desc": {
    zh: "基于 Qwen 3.5 开源模型在科学语料上微调，中英双语支持，适合中文科研团队使用，在化学和生物医学文本理解上表现突出。",
    en: "Fine-tuned on scientific corpus based on the open-source Qwen 3.5 model, with bilingual Chinese-English support. Ideal for Chinese research teams, excels in chemical and biomedical text understanding.",
  },
  "data.llm.mini.desc": {
    zh: "轻量级科学 LLM，专为低延迟场景设计，适合实时分子属性筛选和快速科学问答，推理速度比旗舰模型快 5 倍。",
    en: "Lightweight scientific LLM designed for low-latency scenarios. Ideal for real-time molecular property screening and quick scientific QA, with 5x faster inference than the flagship model.",
  },
  // Compute Models
  "data.cm.alphafold3.desc": {
    zh: "预测蛋白质、DNA、RNA 及小分子配体的复合物结构，CASP15 冠军模型，精度远超前代。",
    en: "Predicts complex structures of proteins, DNA, RNA, and small molecule ligands. CASP15 champion model with accuracy far surpassing its predecessors.",
  },
  "data.cm.esmfold.desc": {
    zh: "基于蛋白质语言模型的高速结构预测，无需 MSA，速度比 AlphaFold2 快 60 倍，适合大规模筛选。",
    en: "High-speed structure prediction based on protein language models. No MSA required, 60x faster than AlphaFold2, ideal for large-scale screening.",
  },
  "data.cm.chai1.desc": {
    zh: "多模态基础模型，支持蛋白质、小分子、核酸和共价修饰的联合结构预测，在 PoseBusters 基准上表现领先。",
    en: "Multimodal foundation model supporting joint structure prediction of proteins, small molecules, nucleic acids, and covalent modifications. Leading performance on PoseBusters benchmark.",
  },
  "data.cm.diffdock.desc": {
    zh: "基于扩散模型的分子对接工具，生成蛋白质-配体结合构象，在 PDBBind 基准上超越传统对接方法。",
    en: "Diffusion model-based molecular docking tool that generates protein-ligand binding conformations. Outperforms traditional docking methods on PDBBind benchmark.",
  },
  "data.cm.proteinmpnn.desc": {
    zh: "蛋白质序列设计模型，给定骨架结构，生成最优氨基酸序列，广泛用于蛋白质工程和抗体设计。",
    en: "Protein sequence design model that generates optimal amino acid sequences given backbone structure. Widely used in protein engineering and antibody design.",
  },
  "data.cm.chemprop.desc": {
    zh: "基于消息传递神经网络的分子性质预测，支持 ADMET、活性、毒性等多种分子属性预测任务。",
    en: "Molecular property prediction based on message-passing neural networks. Supports various prediction tasks including ADMET, activity, and toxicity.",
  },
  "data.cm.unimol.desc": {
    zh: "通用分子表示学习模型，支持小分子、蛋白质和材料的统一表示，在多个分子性质预测基准上达到 SOTA。",
    en: "Universal molecular representation learning model supporting unified representations of small molecules, proteins, and materials. Achieves SOTA on multiple molecular property prediction benchmarks.",
  },
  "data.cm.deepxde.desc": {
    zh: "物理信息神经网络（PINN）框架，求解偏微分方程，适用于流体力学、热传导、量子化学等物理模拟场景。",
    en: "Physics-Informed Neural Network (PINN) framework for solving PDEs. Applicable to fluid dynamics, heat conduction, quantum chemistry, and other physics simulation scenarios.",
  },
  // Databases
  "data.db.reaction.desc": {
    zh: "大规模有机反应数据库，收录超过 1.2 亿条有机反应记录，含反应物、产物、反应条件、产率和文献来源。",
    en: "Large-scale organic reaction database with over 120 million reaction records, including reactants, products, reaction conditions, yields, and literature sources.",
  },
  "data.db.materials.desc": {
    zh: "晶体结构与材料性质数据库，覆盖有机、无机和金属有机框架材料，含电子、力学、热力学性质。",
    en: "Crystal structure and material properties database covering organic, inorganic, and metal-organic framework materials, including electronic, mechanical, and thermodynamic properties.",
  },
  "data.db.bioactivity.desc": {
    zh: "小分子生物活性数据库，收录超过 2000 万条小分子对多靶点的活性数据（IC₅₀、Ki、EC₅₀），来源于文献和实验。",
    en: "Small molecule bioactivity database with over 20 million activity records (IC₅₀, Ki, EC₅₀) against multiple targets, sourced from literature and experiments.",
  },
  "data.db.admet.desc": {
    zh: "ADMET 实验数据库，涵盖吸收、分布、代谢、排泄和毒性的实验测定值，用于 ADMET 预测模型训练和成药性评估。",
    en: "ADMET experimental database covering absorption, distribution, metabolism, excretion, and toxicity measurements. Used for ADMET prediction model training and drugability assessment.",
  },
  // Workflow Templates
  "data.wf.t2l.name": { zh: "靶点到先导化合物筛选", en: "Target-to-Lead Screening" },
  "data.wf.t2l.desc": {
    zh: "从靶蛋白序列出发，自动完成结构预测、虚拟筛选、分子对接和 ADMET 评估的完整流程。",
    en: "Starting from target protein sequence, automatically completes structure prediction, virtual screening, molecular docking, and ADMET evaluation.",
  },
  "data.wf.t2l.step1": { zh: "蛋白结构预测 (AlphaFold3)", en: "Protein Structure Prediction (AlphaFold3)" },
  "data.wf.t2l.step2": { zh: "分子对接 (DiffDock)", en: "Molecular Docking (DiffDock)" },
  "data.wf.t2l.step3": { zh: "ADMET 筛选 (Chemprop)", en: "ADMET Screening (Chemprop)" },
  "data.wf.t2l.step4": { zh: "结果报告生成", en: "Result Report Generation" },
  "data.wf.pd.name": { zh: "蛋白质序列设计与验证", en: "Protein Sequence Design & Validation" },
  "data.wf.pd.desc": {
    zh: "给定蛋白质骨架结构，设计最优氨基酸序列，并通过结构预测验证设计结果。",
    en: "Given protein backbone structure, designs optimal amino acid sequences and validates results through structure prediction.",
  },
  "data.wf.pd.step1": { zh: "序列设计 (ProteinMPNN)", en: "Sequence Design (ProteinMPNN)" },
  "data.wf.pd.step2": { zh: "结构验证 (ESMFold)", en: "Structure Validation (ESMFold)" },
  "data.wf.pd.step3": { zh: "活性数据库比对", en: "Activity Database Comparison" },
  "data.wf.pd.step4": { zh: "设计报告导出", en: "Design Report Export" },
  "data.wf.ms.name": { zh: "材料性质预测与筛选", en: "Material Property Prediction & Screening" },
  "data.wf.ms.desc": {
    zh: "从材料数据库检索候选材料，预测关键性质，生成筛选报告。",
    en: "Retrieves candidate materials from databases, predicts key properties, and generates screening reports.",
  },
  "data.wf.ms.step1": { zh: "材料数据库检索", en: "Material Database Retrieval" },
  "data.wf.ms.step2": { zh: "性质预测 (Uni-Mol)", en: "Property Prediction (Uni-Mol)" },
  "data.wf.ms.step3": { zh: "多维度排序筛选", en: "Multi-dimensional Ranking & Screening" },
  "data.wf.ms.step4": { zh: "可视化报告生成", en: "Visual Report Generation" },
  "data.wf.lh.name": { zh: "文献驱动的假设生成", en: "Literature-Driven Hypothesis Generation" },
  "data.wf.lh.desc": {
    zh: "基于科学 LLM 分析相关文献，结合知识图谱检索，生成可验证的科学假设和实验方案。",
    en: "Analyzes relevant literature using Science LLM, combined with knowledge graph retrieval, to generate verifiable scientific hypotheses and experimental protocols.",
  },
  "data.wf.lh.step1": { zh: "文献检索与分析 (Science LLM)", en: "Literature Search & Analysis (Science LLM)" },
  "data.wf.lh.step2": { zh: "知识图谱推理", en: "Knowledge Graph Reasoning" },
  "data.wf.lh.step3": { zh: "假设生成与评估", en: "Hypothesis Generation & Evaluation" },
  "data.wf.lh.step4": { zh: "实验方案建议", en: "Experimental Protocol Suggestions" },
  // Pricing table
  "data.pt.computeModels": { zh: "科学计算模型", en: "Compute Models" },
  "data.pt.computeDim": { zh: "GPU 算力时长 + 输入数据规模", en: "GPU compute time + input data scale" },
  "data.pt.computeLogic": { zh: "按任务类型设定基础费率，乘以序列长度/分子数量系数", en: "Base rate set by task type, multiplied by sequence length/molecule count coefficient" },
  "data.pt.llm": { zh: "科学 LLM", en: "Science LLM" },
  "data.pt.llmDim": { zh: "输入 Token 数 + 输出 Token 数", en: "Input tokens + Output tokens" },
  "data.pt.llmLogic": { zh: "与通用 LLM 计费逻辑对齐，旗舰模型溢价计费", en: "Aligned with general LLM pricing, premium pricing for flagship models" },
  "data.pt.dbQuery": { zh: "数据库查询", en: "Database Query" },
  "data.pt.dbDim": { zh: "查询条数 + 返回数据量", en: "Query count + returned data volume" },
  "data.pt.dbLogic": { zh: "按查询次数计费，批量导出按数据集大小计费", en: "Billed by query count, bulk export billed by dataset size" },
  "data.pt.agent": { zh: "SciNexus Agent", en: "SciNexus Agent" },
  "data.pt.agentDim": { zh: "工作流步骤数 + 底层服务消耗", en: "Workflow steps + underlying service consumption" },
  "data.pt.agentLogic": { zh: "按 Agent 编排的各底层服务实际消耗汇总，附加 Agent 编排服务费", en: "Aggregated actual consumption of underlying services orchestrated by Agent, plus orchestration fee" },
  // Mock API key names
  "data.key.prod": { zh: "生产环境 - 药物研发项目", en: "Production - Drug Discovery Project" },
  "data.key.dev": { zh: "开发测试 Key", en: "Development Test Key" },
  // Demo messages
  "data.demo.userMsg": {
    zh: "我需要预测 CDK2 激酶（PDB: 1HCL）与候选抑制剂的结合模式，候选分子的 SMILES 为：CC1=NC(=C(C=C1)C2=CC=CC=C2)NC(=O)C3=CC=CN=C3",
    en: "I need to predict the binding mode of CDK2 kinase (PDB: 1HCL) with a candidate inhibitor. The SMILES of the candidate molecule is: CC1=NC(=C(C=C1)C2=CC=CC=C2)NC(=O)C3=CC=CN=C3",
  },
  "data.demo.assistantMsg": {
    zh: "好的，我将为您执行完整的分子对接流程。正在分解任务...",
    en: "Sure, I'll execute the complete molecular docking workflow for you. Breaking down the tasks...",
  },
  "data.demo.step1": { zh: "获取 CDK2 蛋白结构", en: "Fetch CDK2 protein structure" },
  "data.demo.step2": { zh: "调用 AlphaFold3 验证结构质量", en: "Call AlphaFold3 to validate structure quality" },
  "data.demo.step3": { zh: "配体准备与构象生成", en: "Ligand preparation and conformer generation" },
  "data.demo.step4": { zh: "分子对接 (DiffDock)", en: "Molecular docking (DiffDock)" },
  "data.demo.step5": { zh: "ADMET 属性预测", en: "ADMET property prediction" },
  "data.demo.step6": { zh: "生成分析报告", en: "Generate analysis report" },
};

// ─── Login & Change Password ───
const loginPage: TranslationMap = {
  "login.title": { zh: "LLM Token Relay Platform", en: "LLM Token Relay Platform" },
  "login.email": { zh: "邮箱", en: "Email" },
  "login.password": { zh: "密码", en: "Password" },
  "login.placeholder.email": { zh: "you@example.com", en: "you@example.com" },
  "login.placeholder.password": { zh: "输入你的密码", en: "Enter your password" },
  "login.button": { zh: "登录", en: "Login" },
  "login.loading": { zh: "登录中...", en: "Logging in..." },
  "login.fillAll": { zh: "请填写所有字段", en: "Please fill in all fields" },
  "login.success": { zh: "登录成功", en: "Logged in successfully" },
  "login.failed": { zh: "登录失败", en: "Login failed" },
  "login.footer": { zh: "账号由团队管理员创建", en: "Account is provisioned by your team admin" },
  "login.captcha.loading": { zh: "正在加载验证码…", en: "Loading captcha…" },
  "login.captcha.notLoaded": { zh: "验证码加载失败，请刷新页面重试", en: "Captcha failed to load, please refresh the page and retry" },
  "login.captcha.failed": { zh: "验证失败，请重新完成验证", en: "Captcha verification failed, please try again" },
  "login.captcha.cancelled": { zh: "验证已取消", en: "Captcha cancelled" },
  "changePwd.title": { zh: "修改密码", en: "Change Password" },
  "changePwd.mustChange": { zh: "首次登录请设置新密码", en: "Please set a new password before continuing" },
  "changePwd.currentPassword": { zh: "当前密码", en: "Current Password" },
  "changePwd.newPassword": { zh: "新密码", en: "New Password" },
  "changePwd.confirmPassword": { zh: "确认新密码", en: "Confirm New Password" },
  "changePwd.placeholder": { zh: "至少 8 个字符", en: "At least 8 characters" },
  "changePwd.submit": { zh: "更新密码", en: "Update Password" },
  "changePwd.submitting": { zh: "更新中...", en: "Updating..." },
  "changePwd.cancel": { zh: "返回", en: "Cancel" },
  "changePwd.signOut": { zh: "退出登录", en: "Sign out" },
  "changePwd.fillAll": { zh: "请填写所有字段", en: "Please fill in all fields" },
  "changePwd.tooShort": { zh: "新密码至少 8 位", en: "New password must be at least 8 characters" },
  "changePwd.sameAsOld": { zh: "新密码必须不同于旧密码", en: "New password must differ from current" },
  "changePwd.mismatch": { zh: "两次输入的新密码不一致", en: "Passwords do not match" },
  "changePwd.success": { zh: "密码已更新", en: "Password updated" },
  "changePwd.rule.min8": { zh: "至少 8 个字符", en: "At least 8 characters" },
  "changePwd.rule.upper": { zh: "包含大写字母", en: "Contains uppercase letter" },
  "changePwd.rule.lower": { zh: "包含小写字母", en: "Contains lowercase letter" },
  "changePwd.rule.digit": { zh: "包含数字", en: "Contains number" },
  "changePwd.match": { zh: "密码一致", en: "Passwords match" },
  "changePwd.noMatch": { zh: "密码不一致", en: "Passwords do not match" },
};

// ─── Science Agent Marketplace ───
const sciAgents: TranslationMap = {
  "console.scienceAgentMarketplace": { zh: "领域模型广场", en: "Domain Models" },
  "sciAgents.title": { zh: "领域模型广场", en: "Domain Model Marketplace" },
  "sciAgents.mockBanner": { zh: "当前展示为模拟数据，领域模型正在开发中", en: "Showing mock data. Domain models are under development." },
  "sciAgents.backToList": { zh: "← 返回领域模型广场", en: "← Back to Domain Models" },
  "sciAgents.startChat": { zh: "开始对话", en: "Start Chat" },
  "sciAgents.viewDocs": { zh: "查看 API 文档", en: "View API Docs" },
  "sciAgents.tabTools": { zh: "工具集", en: "Tools" },
  "sciAgents.tabDatasources": { zh: "数据源", en: "Data Sources" },
  "sciAgents.tabScenarios": { zh: "使用场景", en: "Scenarios" },
  "sciAgents.tabCode": { zh: "代码示例", en: "Code Examples" },
  "sciAgents.comingSoon": { zh: "即将上线", en: "Coming Soon" },
  "sciAgents.comingSoonToast": { zh: "领域模型正在开发中，敬请期待", en: "Domain models are under development. Stay tuned!" },
};

// ─── Experience Center ───
const experienceCenter: TranslationMap = {
  "console.experienceCenter": { zh: "体验中心", en: "EXPERIENCE" },
  "console.expSciLLM": { zh: "科学大模型", en: "Science LLM" },
  "experienceCenter.title": { zh: "科学大模型体验", en: "Science LLM Experience" },
};

// ─── Common ───
const common: TranslationMap = {
  "common.home": { zh: "首页", en: "Home" },
  "common.copied": { zh: "已复制!", en: "Copied!" },
  "common.copy": { zh: "复制", en: "Copy" },
  // Relative time (used by adapters.ts — not directly in JSX)
  "time.never": { zh: "从未", en: "Never" },
  "time.justNow": { zh: "刚刚", en: "Just now" },
  "time.minAgo": { zh: "{n} 分钟前", en: "{n} min ago" },
  "time.hrAgo": { zh: "{n} 小时前", en: "{n} hr ago" },
  "time.dayAgo": { zh: "{n} 天前", en: "{n} day(s) ago" },
  "domain.proteinStructure": { zh: "蛋白质结构预测", en: "Protein Structure" },
  "domain.proteinDesign": { zh: "蛋白质/抗体设计", en: "Protein Design" },
  "domain.proteinProperty": { zh: "蛋白质性质预测", en: "Protein Property" },
  "domain.molecularGeneration": { zh: "分子生成与设计", en: "Molecular Generation" },
  "domain.drugDiscovery": { zh: "药物发现", en: "Drug Discovery" },
  "domain.scientificIntelligence": { zh: "科学智能", en: "Scientific Intelligence" },
};

export const translations: TranslationMap = {
  ...navbar,
  ...footer,
  ...home,
  ...llms,
  ...models,
  ...data,
  ...scinexus,
  ...notFound,
  ...console_layout,
  ...admin,
  ...dashboard,
  ...apiKeys,
  ...billing,
  ...settings,
  ...team,
  ...teamMgmt,
  ...teamBills,
  ...billPdf,
  ...logs,
  ...consoleScinexus,
  ...dataDesc,
  ...loginPage,
  ...sciAgents,
  ...experienceCenter,
  ...common,
};
